import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import {
  ConditionFeatureFlag,
  IActionType,
  VARIABLE_TEXT_FEATURE_FLAG_ALL,
  VariableSelectFeatureFlag,
  VariableTextFeatureFlag,
} from '@makyoapp/makyo-common'

import { TEXT_ABLE_COLUMN_TYPES_TEXT_ONLY } from '@/shared/builder-constants'

import {
  AspectRatio,
  BuilderComponentGroup,
  BuilderComponentSettingType,
  ComponentSettingKey,
  getRatioLabel,
  MdGalleryLayout,
  MdIconButtonVariant,
  MdShape,
  SpacingInputUnit,
} from '../types'
import { LABELS } from '../types/labels'
import {
  hoverEffectOptions,
  makeClampWidthItems,
  makeDataSource,
  makeFilter,
  makeSorting,
  makeTextColorSettings,
  makeTypographyGroup,
} from '../utils/components'

const textOnlyColumnTypes = TEXT_ABLE_COLUMN_TYPES_TEXT_ONLY.filter(
  (type) => type !== ColumnTypes.Rating && type !== ColumnTypes.Slug
)

const withoutSlugColumnTypes = TEXT_ABLE_COLUMN_TYPES_TEXT_ONLY.filter((type) => type !== ColumnTypes.Slug)

export const ListComponents: BuilderComponentGroup = {
  id: 'list',
  title: 'Lists',
  components: [
    // list
    {
      compId: 'list',
      title: 'List',
      searchKeywords: ['list', 'bullet list', 'checklist', 'ordered list', 'unordered list', 'items', 'to-do list'],
      icon: 'List24',
      nested: false,
      settings: {
        dataGroup: {
          [LABELS.DATA_SOURCE_KEY]: {
            items: [
              makeDataSource(
                ComponentSettingKey.ListDataSource,
                'Select a table from your database to serve as the data source for this list. This will determine the available data for other selections, such as title, image, and badge metadata.'
              ),
              {
                id: ComponentSettingKey.ListDataTitle,
                label: LABELS.LINE1_KEY,
                help: 'The title must be sourced from a column containing text data. Columns with non-text data types will not appear in the dropdown menu.',
                type: BuilderComponentSettingType.ColumnSelect,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.ListDataSource,
                  columnTypes: [
                    ColumnTypes.Text,
                    ColumnTypes.RichText,
                    ColumnTypes.Function,
                    ColumnTypes.URL,
                    ColumnTypes.Email,
                    ColumnTypes.Address,
                    ColumnTypes.Boolean,
                    ColumnTypes.Coordinate,
                    ColumnTypes.DateTime,
                    ColumnTypes.Join,
                    ColumnTypes.Color,
                    ColumnTypes.Number,
                    ColumnTypes.Phone,
                    ColumnTypes.RowID,
                    ColumnTypes.Lookup,
                  ],
                  includeLookupType: true,
                },
              },
              {
                id: ComponentSettingKey.ListDataText,
                label: LABELS.LINE2_KEY,
                // help: 'The description must be sourced from a column containing text data. Columns with non-text data types will not appear in the dropdown menu.',
                type: BuilderComponentSettingType.ColumnSelect,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.ListDataSource,
                  columnTypes: [
                    ColumnTypes.Text,
                    ColumnTypes.RichText,
                    ColumnTypes.Function,
                    ColumnTypes.URL,
                    ColumnTypes.Email,
                    ColumnTypes.Address,
                    ColumnTypes.Boolean,
                    ColumnTypes.Coordinate,
                    ColumnTypes.DateTime,
                    ColumnTypes.Join,
                    ColumnTypes.Color,
                    ColumnTypes.Number,
                    ColumnTypes.Phone,
                    ColumnTypes.RowID,
                    ColumnTypes.Lookup,
                  ],
                  includeLookupType: true,
                },
              },
              {
                id: ComponentSettingKey.ListDataText3,
                label: LABELS.LINE3_KEY,
                // help: 'The description must be sourced from a column containing text data. Columns with non-text data types will not appear in the dropdown menu.',
                type: BuilderComponentSettingType.ColumnSelect,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.ListDataSource,
                  columnTypes: [
                    ColumnTypes.Text,
                    ColumnTypes.RichText,
                    ColumnTypes.Function,
                    ColumnTypes.URL,
                    ColumnTypes.Email,
                    ColumnTypes.Address,
                    ColumnTypes.Boolean,
                    ColumnTypes.Coordinate,
                    ColumnTypes.DateTime,
                    ColumnTypes.Join,
                    ColumnTypes.Color,
                    ColumnTypes.Number,
                    ColumnTypes.Phone,
                    ColumnTypes.RowID,
                    ColumnTypes.Lookup,
                  ],
                  includeLookupType: true,
                },
              },
              {
                id: ComponentSettingKey.ListDataMedia,
                help: 'Only columns containing image types or icons will appear in the dropdown menu. If no such columns exist, the dropdown menu will be grayed out and unselectable.',
                label: LABELS.MEDIA_KEY,
                type: BuilderComponentSettingType.ColumnSelect,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.ListDataSource,
                  columnTypes: [
                    ColumnTypes.Image,
                    ColumnTypes.Icon,
                    ColumnTypes.Lookup,
                    ColumnTypes.Barcode,
                    ColumnTypes.QRCode,
                    ColumnTypes.Function,
                  ],
                  includeLookupType: true,
                },
              },
              {
                id: ComponentSettingKey.ListDataBadge,
                label: LABELS.BADGE_METADATA,
                // help: 'Badge metadata must be sourced from a column containing numerical data. Columns with non-numeric data types will not be available in the dropdown menu.',
                type: BuilderComponentSettingType.ColumnSelect,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.ListDataSource,
                  columnTypes: textOnlyColumnTypes,
                  includeLookupType: true,
                  noAutoSelect: true,
                },
              },
              {
                id: ComponentSettingKey.ListDataTrailingMetadata,
                label: LABELS.METADATA_KEY,
                help: 'Accepted DB Column Types: Text, Rating, Number, URL, Phone, Email, Address, and Function',
                type: BuilderComponentSettingType.ColumnSelect,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.ListDataSource,
                  columnTypes: [
                    ColumnTypes.Text,
                    ColumnTypes.Number,
                    ColumnTypes.DateTime,
                    ColumnTypes.Phone,
                    ColumnTypes.Address,
                    ColumnTypes.URL,
                    ColumnTypes.Join,
                    ColumnTypes.Function,
                    ColumnTypes.Rating,
                    ColumnTypes.Lookup,
                    ColumnTypes.Email,
                  ],
                  includeLookupType: true,
                },
                // dependOn: {
                //   id: ComponentSettingKey.ListDataTrailingType,
                //   showWhen: (value) => {
                //     const v = Array.isArray(value) ? value : []
                //     return v.includes('metadata')
                //   },
                // },
              },
            ],
          },
          'Item Actions': {
            items: [
              {
                id: ComponentSettingKey.ListDataTrailingType,
                label: '',
                type: BuilderComponentSettingType.GroupSorting,
                groupSortingSettings: {
                  Menu: {
                    items: [
                      {
                        id: ComponentSettingKey.ListDataTrailingMenu,
                        label: 'Action Menu',
                        help: 'Open contextual menu with more options for this item.',
                        type: BuilderComponentSettingType.MenuSelect,
                        menuSelectSettings: {
                          autoDefault: true,
                        },
                      },
                    ],
                  },
                  Checkbox: {
                    items: [
                      {
                        id: ComponentSettingKey.ListDataTrailingToggleCheckBox,
                        label: 'Multi-Select',
                        help: 'Select multiple items to perform bulk actions.',
                        type: BuilderComponentSettingType.Switch,
                        defaultValue: false,
                      },
                    ],
                  },
                  'Toggle Icon': {
                    items: [
                      {
                        id: ComponentSettingKey.ListDataTrailingToggleSwitch,
                        label: 'Toggle State',
                        help: 'Switch this item on or off. Linked to a boolean column in your table.',
                        type: BuilderComponentSettingType.Switch,
                        defaultValue: false,
                      },
                      {
                        id: ComponentSettingKey.ListDataTrailingToggle,
                        label: LABELS.DATA_COLUMN_KEY,
                        help: 'Accepted DB Column Types: Boolean',
                        type: BuilderComponentSettingType.ColumnSelect,
                        columnSelectSettings: {
                          tableSourceId: ComponentSettingKey.ListDataSource,
                          columnTypes: [ColumnTypes.Boolean],
                          noAutoSelect: true,
                          includeLookupType: true,
                        },
                        dependOn: {
                          id: ComponentSettingKey.ListDataTrailingToggleSwitch,
                          showWhen: (value) => {
                            return !!value
                          },
                        },
                      },
                    ],
                  },
                  'Action Icon': {
                    items: [
                      {
                        id: ComponentSettingKey.ListDataTrailingToggleIconButton,
                        label: 'Quick Action',
                        type: BuilderComponentSettingType.Switch,
                        defaultValue: false,
                        help: 'Run a direct action on this item (open details, delete, etc.).',
                      },
                      {
                        id: ComponentSettingKey.ListDataTrailingIconButtonIcon,
                        label: 'Icon Button',
                        type: BuilderComponentSettingType.Icon,
                        defaultValue: 'arrow_right',
                        dependOn: {
                          id: ComponentSettingKey.ListDataTrailingToggleIconButton,
                          showWhen: (value) => {
                            return !!value
                          },
                        },
                      },
                    ],
                  },
                },
              },

              // [LABELS.ACTION_MENU_KEY]: {
              //   items: [
              // {
              //   id: ComponentSettingKey.ListDataTrailingMenu,
              //   label: 'Select Menu',
              //   help: 'Select any menu from the Menu tab — it will appear as a three-dot menu at the end of the list item.',
              //   type: BuilderComponentSettingType.MenuSelect,
              //   menuSelectSettings: {
              //     autoDefault: true,
              //   },
              // },
              //   ],
              // },
              // 'Check Box': {
              //   items: [
              // {
              //   id: ComponentSettingKey.ListDataTrailingToggleCheckBox,
              //   label: 'Allow multiple items selection',
              //   type: BuilderComponentSettingType.Switch,
              //   defaultValue: false,
              // },
              //   ],
              // },
              // 'Switch Box': {
              //   items: [
              // {
              //   id: ComponentSettingKey.ListDataTrailingToggleSwitch,
              //   label: 'Switch Box',
              //   type: BuilderComponentSettingType.Switch,
              //   defaultValue: false,
              // },
              // {
              //   id: ComponentSettingKey.ListDataTrailingToggle,
              //   label: LABELS.SWITCH_METADATA_KEY,
              //   help: 'Accepted DB Column Types: Boolean',
              //   type: BuilderComponentSettingType.ColumnSelect,
              //   columnSelectSettings: {
              //     tableSourceId: ComponentSettingKey.ListDataSource,
              //     columnTypes: [ColumnTypes.Boolean],
              //     noAutoSelect: true,
              //     includeLookupType: true,
              //   },
              //   dependOn: {
              //     id: ComponentSettingKey.ListDataTrailingToggleSwitch,
              //     showWhen: (value) => {
              //       return !!value
              //       // const v = Array.isArray(value) ? value : []
              //       // return v.includes('toggle')
              //     },
              //   },
              // },
              //   ],
              // },
              // 'Action Icon': {
              //   items: [
              //     {
              //       id: ComponentSettingKey.ListDataTrailingToggleIconButton,
              //       label: 'Action Icon',
              //       type: BuilderComponentSettingType.Switch,
              //       defaultValue: false,
              //     },
              //     {
              //       id: ComponentSettingKey.ListDataTrailingIconButtonIcon,
              //       label: LABELS.BUTTON_ICON_KEY,
              //       type: BuilderComponentSettingType.Icon,
              //       defaultValue: 'arrow_right',
              //       dependOn: {
              //         id: ComponentSettingKey.ListDataTrailingToggleIconButton,
              //         showWhen: (value) => {
              //           return !!value
              //           // const v = Array.isArray(value) ? value : []
              //           // return v.includes('icon-button')
              //         },
              //       },
              //     },
              //   ],
              // },
            ],
          },
          // [LABELS.INTERACTION_COMPONENTS_KEY]: {
          //   items: [
          //     {
          //       id: ComponentSettingKey.ListDataTrailingType,
          //       type: BuilderComponentSettingType.Select,
          //       label: LABELS.TRAILING_ELEMENT_TYPE_KEY,
          //       defaultValue: [],
          //       selectSettings: {
          //         multiple: true,
          //         options: [
          //           {
          //             value: 'checkbox',
          //             icon: 'Checkbox16',
          //             label: LABELS.CHECK_BOX_KEY,
          //           },
          //           {
          //             value: 'menu',
          //             icon: 'Menu16',
          //             label: LABELS.MENU_KEY,
          //           },
          //           {
          //             value: 'metadata',
          //             icon: 'Db16',
          //             label: LABELS.METADATA_KEY,
          //           },
          //           {
          //             value: 'toggle',
          //             icon: 'Toggle16',
          //             label: LABELS.SWITCH_KEY,
          //           },
          //           {
          //             value: 'icon-button',
          //             icon: 'Icon16',
          //             label: LABELS.ICON_BUTTON_KEY,
          //           },
          //         ],
          //       },
          //     },
          //     {
          //       id: ComponentSettingKey.ListDataTrailingMenu,
          //       label: LABELS.MENU_KEY,
          //       help: 'Select menu created in Menu Tab',
          //       type: BuilderComponentSettingType.MenuSelect,
          //       menuSelectSettings: {
          //         autoDefault: true,
          //       },
          //       dependOn: {
          //         id: ComponentSettingKey.ListDataTrailingType,
          //         showWhen: (value) => {
          //           const v = Array.isArray(value) ? value : []
          //           return v.includes('menu')
          //         },
          //       },
          //     },
          //     {
          //       id: ComponentSettingKey.ListDataTrailingMetadata,
          //       label: LABELS.METADATA_KEY,
          //       help: 'Accepted DB Column Types: Text, Rating, Number, URL, Phone, Email, Address, and Function',
          //       type: BuilderComponentSettingType.ColumnSelect,
          //       columnSelectSettings: {
          //         tableSourceId: ComponentSettingKey.ListDataSource,
          //         columnTypes: [
          //           ColumnTypes.Text,
          //           ColumnTypes.Number,
          //           ColumnTypes.DateTime,
          //           ColumnTypes.Phone,
          //           ColumnTypes.Address,
          //           ColumnTypes.URL,
          //           ColumnTypes.Join,
          //           ColumnTypes.Function,
          //           ColumnTypes.Rating,
          //           ColumnTypes.Lookup,
          //           ColumnTypes.Email,
          //         ],
          //         includeLookupType: true,
          //       },
          //       dependOn: {
          //         id: ComponentSettingKey.ListDataTrailingType,
          //         showWhen: (value) => {
          //           const v = Array.isArray(value) ? value : []
          //           return v.includes('metadata')
          //         },
          //       },
          //     },
          //     {
          //       id: ComponentSettingKey.ListDataTrailingToggle,
          //       label: LABELS.SWITCH_METADATA_KEY,
          //       help: 'Accepted DB Column Types: Boolean',
          //       type: BuilderComponentSettingType.ColumnSelect,
          //       columnSelectSettings: {
          //         tableSourceId: ComponentSettingKey.ListDataSource,
          //         columnTypes: [ColumnTypes.Boolean],
          //         noAutoSelect: true,
          //         includeLookupType: true,
          //       },
          //       dependOn: {
          //         id: ComponentSettingKey.ListDataTrailingType,
          //         showWhen: (value) => {
          //           const v = Array.isArray(value) ? value : []
          //           return v.includes('toggle')
          //         },
          //       },
          //     },
          //     {
          //       id: ComponentSettingKey.ListDataTrailingToggleUserSpec,
          //       label: LABELS.SWITCH_USER_SPECIFIC_KEY,
          //       help: 'Use user specific value from database',
          //       type: BuilderComponentSettingType.Switch,
          //       defaultValue: true,
          //       dependOn: {
          //         id: ComponentSettingKey.ListDataTrailingType,
          //         showWhen: (value) => {
          //           const v = Array.isArray(value) ? value : []
          //           return v.includes('toggle')
          //         },
          //       },
          //     },
          //     {
          //       id: ComponentSettingKey.ListDataTrailingToggleIcon,
          //       label: LABELS.SWITCH_ICON_KEY,
          //       type: BuilderComponentSettingType.Icon,
          //       defaultValue: 'favorite',
          //       dependOn: {
          //         id: ComponentSettingKey.ListDataTrailingType,
          //         showWhen: (value) => {
          //           const v = Array.isArray(value) ? value : []
          //           return v.includes('toggle')
          //         },
          //       },
          //     },
          //     {
          //       id: ComponentSettingKey.ListDataTrailingIconButtonIcon,
          //       label: LABELS.BUTTON_ICON_KEY,
          //       type: BuilderComponentSettingType.Icon,
          //       defaultValue: 'arrow_right',
          //       dependOn: {
          //         id: ComponentSettingKey.ListDataTrailingType,
          //         showWhen: (value) => {
          //           const v = Array.isArray(value) ? value : []
          //           return v.includes('icon-button')
          //         },
          //       },
          //     },
          //   ],
          // },
          [LABELS.COMPONENT_TITLE_KEY]: {
            items: [
              {
                id: ComponentSettingKey.ListDataCompTitle,
                type: BuilderComponentSettingType.VariableText,
                label: '',
                defaultValue: [
                  {
                    type: 'paragraph',
                    children: [{ text: '' }],
                  },
                ],
                variableTextSettings: {
                  flags:
                    VARIABLE_TEXT_FEATURE_FLAG_ALL &
                    ~VariableTextFeatureFlag.ColumnData &
                    ~VariableTextFeatureFlag.Image,
                },
              },
            ],
          },
          [LABELS.SORTING_KEY]: makeSorting(
            ComponentSettingKey.ListDataSortBy,
            ComponentSettingKey.ListDataSortType,
            ComponentSettingKey.ListDataSource
          ),
          [LABELS.DATA_FILTER_KEY]: makeFilter(ComponentSettingKey.ListDataFilter, ComponentSettingKey.ListDataSource),
          [LABELS.GROUPING_KEY]: {
            items: [
              {
                id: ComponentSettingKey.ListDataGroupBy,
                label: LABELS.GROUP_BY_KEY,
                help: 'Only works when in vertical layout mode',
                type: BuilderComponentSettingType.ColumnSelect,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.ListDataSource,
                  noAutoSelect: true,
                  includeLookupType: true,
                  columnTypes: [
                    ColumnTypes.Text,
                    ColumnTypes.RichText,
                    ColumnTypes.Function,
                    ColumnTypes.URL,
                    ColumnTypes.Email,
                    ColumnTypes.Address,
                    ColumnTypes.Boolean,
                    ColumnTypes.Coordinate,
                    ColumnTypes.DateTime,
                    ColumnTypes.Join,
                    ColumnTypes.Color,
                    ColumnTypes.Number,
                    ColumnTypes.Phone,
                    ColumnTypes.RowID,
                    ColumnTypes.Lookup,
                  ],
                },
              },
            ],
          },
        },
        designGroup: {
          [LABELS.COLORS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.ListDesignColorMode,
                label: 'Color Mode',
                type: BuilderComponentSettingType.Select,
                selectSettings: {
                  options: [
                    { value: 'static', label: 'Static' },
                    { value: 'random', label: 'Random' },
                    { value: 'sequential', label: 'Sequential' },
                  ],
                },
                defaultValue: 'static',
              },
              {
                id: ComponentSettingKey.ListDesignColorToneOrder,
                label: 'Color Tone Order',
                type: BuilderComponentSettingType.IconSelect,
                iconSelectSettings: {
                  options: [
                    { id: 'asc', title: 'Ascending', icon: 'Asc16' },
                    { id: 'desc', title: 'Descending', icon: 'Desc16' },
                  ],
                },
                defaultValue: 'asc',
                dependOn: {
                  id: ComponentSettingKey.ListDesignColorMode,
                  showWhen: (v) => v === 'sequential',
                },
              },
              {
                id: ComponentSettingKey.ListDesignColorStartPalette,
                label: 'Color Start Palette',
                type: BuilderComponentSettingType.Select,
                selectSettings: {
                  options: [
                    { value: 'primary', label: 'Primary' },
                    { value: 'secondary', label: 'Secondary' },
                    { value: 'tertiary', label: 'Tertiary' },
                  ],
                },
                dependOn: {
                  id: ComponentSettingKey.ListDesignColorMode,
                  showWhen: (v) => v === 'sequential',
                },
              },
              {
                id: ComponentSettingKey.ListDesignItemBgColor,
                type: BuilderComponentSettingType.ColorVariant,
                label: LABELS.ITEM_BACKGROUND_KEY,
                defaultValue: 'primary-100',
                colorVariantSettings: {
                  fullColor: 'bg',
                  schemeId: ComponentSettingKey.ListDesignScheme,
                },
              },
              {
                id: ComponentSettingKey.ListDesignItemBgOpacity,
                label: LABELS.ITEM_BACKGROUND_OPACITY_KEY,
                type: BuilderComponentSettingType.Slider,
                defaultValue: [100],
                sliderSettings: {
                  step: 1,
                  max: 100,
                  min: 0,
                },
              },
              {
                id: ComponentSettingKey.ListDesignMetadataBgColor,
                type: BuilderComponentSettingType.ColorVariant,
                label: 'Metadata Background',
                defaultValue: 'primary-90',
                colorVariantSettings: {
                  fullColor: 'bg',
                  schemeId: ComponentSettingKey.ListDesignScheme,
                },
              },
              // {
              //   id: ComponentSettingKey.ListDesignCompTitleColor,
              //   type: BuilderComponentSettingType.ColorVariant,
              //   label: LABELS.COMPONENT_TITLE_KEY,
              //   defaultValue: 'on-primary',
              //   colorVariantSettings: {
              //     fullColor: 'fg',
              //     schemeId: ComponentSettingKey.ListDesignScheme,
              //   },
              // },
              // {
              //   id: ComponentSettingKey.ListDesignGroupHeaderColor,
              //   type: BuilderComponentSettingType.ColorVariant,
              //   label: LABELS.GROUP_HEADER_KEY,
              //   defaultValue: 'on-primary',
              //   colorVariantSettings: {
              //     fullColor: 'fg',
              //     schemeId: ComponentSettingKey.ListDesignScheme,
              //   },
              // },
              // {
              //   id: ComponentSettingKey.ListDesignRatingColorVariant,
              //   type: BuilderComponentSettingType.ColorVariant,
              //   label: LABELS.RATING_METADATA_KEY,
              //   help: 'Did you know? You can add rating in trailing element using metadata field and select rating column type.',
              //   colorVariantSettings: {
              //     fullColor: 'fg',
              //     schemeId: ComponentSettingKey.ListDesignScheme,
              //   },
              // },
              // {
              //   id: ComponentSettingKey.ListDesignTitleColor,
              //   type: BuilderComponentSettingType.ColorVariant,
              //   label: LABELS.TITLE_KEY,
              //   defaultValue: 'on-primary',
              //   colorVariantSettings: {
              //     fullColor: 'fg',
              //     schemeId: ComponentSettingKey.ListDesignScheme,
              //   },
              // },
              // {
              //   id: ComponentSettingKey.ListDesignSupportingTextColor,
              //   type: BuilderComponentSettingType.ColorVariant,
              //   label: LABELS.SUPPORTING_TEXT_KEY,
              //   defaultValue: 'on-primary',
              //   colorVariantSettings: {
              //     fullColor: 'fg',
              //     schemeId: ComponentSettingKey.ListDesignScheme,
              //   },
              // },
              {
                id: ComponentSettingKey.ListDesignBadgeColor,
                type: BuilderComponentSettingType.ColorVariant,
                label: LABELS.BADGE_METADATA,
                defaultValue: 'primary-20',
                colorVariantSettings: {
                  fullColor: 'bg',
                  schemeId: ComponentSettingKey.ListDesignScheme,
                },
              },
              {
                id: ComponentSettingKey.ListDesignSearchBgColor,
                type: BuilderComponentSettingType.ColorVariant,
                label: 'Search Box Background',
                defaultValue: 'primary-90',
              },
              {
                id: ComponentSettingKey.ListDesignFilterChipColor,
                type: BuilderComponentSettingType.ColorVariant,
                label: LABELS.FILTERS_CHIP_KEY,
                defaultValue: 'primary-95',
              },
              {
                id: ComponentSettingKey.ListDesignFilterBgColor,
                type: BuilderComponentSettingType.ColorVariant,
                label: LABELS.FILTERS_BG_KEY,
                defaultValue: 'primary-95',
              },
              {
                id: ComponentSettingKey.ListDesignAddButtonColor,
                type: BuilderComponentSettingType.ColorVariant,
                label: 'Add Button Color',
                defaultValue: 'primary-20',
              },
            ],
          },
          [LABELS.DIMENSION_KEY]: {
            items: [
              ...makeClampWidthItems(ComponentSettingKey.ListDesignWidth, {
                label: 'List',
              }).items,
              {
                id: ComponentSettingKey.ListDesignImageWidth,
                type: BuilderComponentSettingType.Slider,
                label: LABELS.IMAGE_KEY,
                defaultValue: [56],
                sliderSettings: {
                  max: 1200,
                  min: 56,
                  step: 1,
                  unit: 'px',
                },
              },
              {
                id: ComponentSettingKey.ListDesignSearchWidth,
                type: BuilderComponentSettingType.Slider,
                label: LABELS.SEARCH_BOX_KEY,
                defaultValue: [100],
                sliderSettings: {
                  max: 100,
                  min: 10,
                  step: 1,
                  range: false,
                  unit: '%',
                },
              },
              {
                id: ComponentSettingKey.ListDesignIconSize,
                type: BuilderComponentSettingType.Slider,
                defaultValue: [24],
                label: LABELS.ICON_SIZE_KEY,
                sliderSettings: {
                  max: 100,
                  min: 18,
                  range: false,
                  step: 1,
                },
              },
            ],
          },
          [LABELS.ASPECT_RATIO_KEY]: {
            items: [
              {
                id: ComponentSettingKey.ListDesignMediaRatio,
                type: BuilderComponentSettingType.Select,
                label: LABELS.IMAGE_KEY,
                defaultValue: AspectRatio.OneByOne,
                selectSettings: {
                  options: Object.values(AspectRatio).map((v) => {
                    return {
                      value: v,
                      label: getRatioLabel(v),
                      icon: 'AspectAuto16',
                    }
                  }),
                },
              },
            ],
          },
          [LABELS.ALIGNMENT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.ListDesignCompHAlign,
                type: BuilderComponentSettingType.ComponentAlignment,
                defaultValue: 'parent',
                label: LABELS.COMPONENT_ALIGNMENT_KEY,
              },
              {
                id: ComponentSettingKey.ListDesignCompTitleAlign,
                type: BuilderComponentSettingType.HorizontalAlignment,
                defaultValue: 'start',
                label: LABELS.COMPONENT_TITLE_KEY,
              },
              {
                id: ComponentSettingKey.ListDesignGroupHeaderAlign,
                type: BuilderComponentSettingType.HorizontalAlignment,
                defaultValue: 'start',
                label: LABELS.GROUP_HEADER_KEY,
              },
              {
                id: ComponentSettingKey.ListDesignGroupSearchAlign,
                type: BuilderComponentSettingType.HorizontalAlignment,
                defaultValue: 'center',
                label: LABELS.SEARCH_BOX_KEY,
              },
              {
                id: ComponentSettingKey.ListDesignPaginationAlignment,
                label: 'Pagination',
                type: BuilderComponentSettingType.HorizontalAlignment,
                defaultValue: 'start',
              },
            ],
          },
          [LABELS.CORNER_RADIUS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.ListItemDesignShape,
                type: BuilderComponentSettingType.Select,
                label: 'List Item',
                defaultValue: MdShape.Medium,
                selectSettings: {
                  options: Object.entries(MdShape).map(([label, value]) => ({
                    value,
                    label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
                  })),
                },
              },
              {
                id: ComponentSettingKey.ListDesignMediaCorner,
                label: LABELS.IMAGE_KEY,
                type: BuilderComponentSettingType.Select,
                defaultValue: MdShape.Small,
                selectSettings: {
                  options: Object.entries(MdShape).map(([label, value]) => ({
                    value,
                    label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
                  })),
                },
              },
              {
                id: ComponentSettingKey.ListDesignSearchCorner,
                label: LABELS.SEARCH_BOX_KEY,
                type: BuilderComponentSettingType.Select,
                defaultValue: MdShape.Medium,
                selectSettings: {
                  options: Object.entries(MdShape).map(([label, value]) => ({
                    value,
                    label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
                  })),
                },
              },
              {
                id: ComponentSettingKey.ListDesignFilterCornerRadius,
                label: LABELS.FILTERS_CHIP_KEY,
                type: BuilderComponentSettingType.Select,
                defaultValue: MdShape.Medium,
                selectSettings: {
                  options: Object.entries(MdShape).map(([label, value]) => ({
                    value,
                    label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
                  })),
                },
              },
              {
                id: ComponentSettingKey.ListDesignAddButtonCornerRadius,
                label: 'Add Button',
                type: BuilderComponentSettingType.Select,
                defaultValue: MdShape.Medium,
                selectSettings: {
                  options: Object.entries(MdShape).map(([label, value]) => ({
                    value,
                    label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
                  })),
                },
              },
            ],
          },
          [LABELS.MEDIA_ICON_KEY]: {
            items: [
              {
                id: ComponentSettingKey.ListDesignIconVariant,
                type: BuilderComponentSettingType.Select,
                label: 'Variant',
                defaultValue: 'filled',
                selectSettings: {
                  options: Object.entries(MdIconButtonVariant)
                    .filter(([k, v]) => v !== MdIconButtonVariant.Elevated && v !== MdIconButtonVariant.FilledTonal)
                    .map(([label, value]) => ({
                      label,
                      value,
                    })),
                },
              },
              {
                id: ComponentSettingKey.ListDesignIconBackgroundColorVariant,
                type: BuilderComponentSettingType.ColorVariant,
                label: LABELS.ICON_BACKGROUND_COLOR_KEY,
                defaultValue: 'primary-80',
                colorVariantSettings: {
                  fullColor: 'bg',
                  schemeId: ComponentSettingKey.ListDesignScheme,
                },
                dependOn: {
                  id: ComponentSettingKey.ListDesignIconVariant,
                  showWhen: (v) => v === 'filled' || v === 'outlined',
                },
              },
              // {
              //   id: ComponentSettingKey.ListDesignIconFilled,
              //   type: BuilderComponentSettingType.Switch,
              //   label: 'Filled',
              //   defaultValue: false,
              // },
              {
                id: ComponentSettingKey.ListDesignIconColorVariant,
                type: BuilderComponentSettingType.ColorVariant,
                label: LABELS.ICON_COLOR_KEY,
                defaultValue: 'primary-20',
                colorVariantSettings: {
                  fullColor: 'bg',
                  schemeId: ComponentSettingKey.ListDesignScheme,
                },
              },
              {
                id: ComponentSettingKey.ListDesignIconColorCustom,
                type: BuilderComponentSettingType.Switch,
                label: 'Dynamic Color',
                defaultValue: false,
              },
              {
                id: ComponentSettingKey.ListDesignIconBackgroundColorColumn,
                type: BuilderComponentSettingType.ColumnSelect,
                label: 'Select Background Color Column',
                help: 'If no color found will fallback to the color variant settings.',
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.ListDataSource,
                  columnTypes: [ColumnTypes.Color, ColumnTypes.Lookup],
                  includeLookupType: true,
                  noAutoSelect: true,
                },
                dependOn: {
                  id: ComponentSettingKey.ListDesignIconColorCustom,
                  showWhen: (v) => v === true,
                },
              },
              {
                id: ComponentSettingKey.ListDesignIconColorColumn,
                type: BuilderComponentSettingType.ColumnSelect,
                label: 'Select Color Column',
                help: 'If no color found will fallback to the color variant settings.',
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.ListDataSource,
                  columnTypes: [ColumnTypes.Color, ColumnTypes.Lookup],
                  includeLookupType: true,
                  noAutoSelect: true,
                },
                dependOn: {
                  id: ComponentSettingKey.ListDesignIconColorCustom,
                  showWhen: (v) => v === true,
                },
              },
              // {
              //   id: ComponentSettingKey.ListDesignIconWeight,
              //   type: BuilderComponentSettingType.Slider,
              //   defaultValue: [200],
              //   label: 'Weight',
              //   sliderSettings: {
              //     max: 700,
              //     min: 100,
              //     range: false,
              //     step: 100,
              //     showMark: true,
              //   },
              // },
              // {
              //   id: ComponentSettingKey.ListDesignIconGrade,
              //   type: BuilderComponentSettingType.SliderSelect,
              //   defaultValue: 0,
              //   label: 'Grade',
              //   sliderSelectSettings: {
              //     options: [-25, 0, 200],
              //   },
              // },
            ],
          },
          // [LABELS.RATING_METADATA_KEY]: {
          //   items: [
          //     {
          //       id: ComponentSettingKey.ListDesignRatingIcon,
          //       type: BuilderComponentSettingType.Icon,
          //       label: 'Icon',
          //       defaultValue: 'star',
          //     },
          //   ],
          // },
          [LABELS.DIVIDER_KEY]: {
            items: [
              {
                id: ComponentSettingKey.ListDesignDivider,
                label: 'Show',
                type: BuilderComponentSettingType.Switch,
                defaultValue: false,
              },
              {
                id: ComponentSettingKey.ListDesignDividerInset,
                label: 'Inset',
                type: BuilderComponentSettingType.Select,
                selectSettings: {
                  options: [
                    {
                      value: 'none',
                      label: 'None',
                    },
                    // {
                    //   value: 'start',
                    //   label: 'Start',
                    // },
                    // {
                    //   value: 'end',
                    //   label: 'End',
                    // },
                    {
                      value: 'both',
                      label: 'Both',
                    },
                  ],
                },
                dependOn: {
                  id: ComponentSettingKey.ListDesignDivider,
                  showWhen: (value) => value === true,
                },
              },
            ],
          },
          // [LABELS.MULTILINE_KEY]: {
          //   items: [
          //     {
          //       id: ComponentSettingKey.ListDesignMultiline,
          //       label: LABELS.SUPPORTING_TEXT_KEY,
          //       defaultValue: true,
          //       type: BuilderComponentSettingType.Switch,
          //     },
          //   ],
          // },
          [LABELS.ORIENTATION_KEY]: {
            items: [
              {
                id: ComponentSettingKey.ListDesignOrientation,
                label: 'Scroll Direction',
                type: BuilderComponentSettingType.IconSelect,
                defaultValue: 'vertical',
                help: 'Horizontal orientation will make the list scrollable horizontally.',
                iconSelectSettings: {
                  options: [
                    {
                      id: 'horizontal',
                      icon: 'Horizontal24',
                      title: 'Horizontal',
                    },
                    {
                      id: 'vertical',
                      icon: 'Vertical24',
                      title: 'Vertical',
                    },
                  ],
                },
              },
            ],
          },
          [LABELS.ITEMS_KEY]: {
            items: [
              // makeNumItems(ComponentSettingKey.ListDataNumItems, 100, 'Dataset Limit'),
              {
                id: ComponentSettingKey.ListDataSetLimit,
                label: 'Dataset Limit',
                type: BuilderComponentSettingType.DataSetLimit,
                defaultValue: {
                  enabled: false,
                  limit: 100,
                  value: 24,
                },
              },
              {
                id: ComponentSettingKey.ListDesignNumCols,
                label: LABELS.COLUMNS_KEY,
                type: BuilderComponentSettingType.Number,
                defaultValue: 1,
                numberSettings: {
                  max: 100,
                  min: 1,
                  step: 1,
                },
              },
              {
                id: ComponentSettingKey.ListDesignNumRows,
                label: LABELS.ROWS_KEY,
                type: BuilderComponentSettingType.Number,
                defaultValue: 1,
                numberSettings: {
                  max: 100,
                  min: 1,
                  step: 1,
                },
              },
              // {
              //   id: ComponentSettingKey.ListDesignColumnRow,
              //   label: 'Display',
              //   type: BuilderComponentSettingType.IconSelect,
              //   iconSelectSettings: {
              //     options: [
              //       {
              //         icon: 'Column16',
              //         id: 'column',
              //         title: 'Column',
              //       },
              //       {
              //         icon: 'Row16',
              //         id: 'row',
              //         title: 'Row',
              //       },
              //     ],
              //   },
              //   dependOn: {
              //     id: ComponentSettingKey.ListDesignOrientation,
              //     showWhen: (v) => v === 'horizontal',
              //   },
              // },
              {
                id: ComponentSettingKey.ListDesignPagination,
                label: LABELS.SHOW_PAGINATION_KEY,
                type: BuilderComponentSettingType.Switch,
                defaultValue: true,
                dependOn: {
                  id: ComponentSettingKey.ListDesignOrientation,
                  showWhen: (v) => v === 'vertical',
                },
              },
              {
                id: ComponentSettingKey.ListDesignHoverEffect,
                type: BuilderComponentSettingType.Switch,
                label: 'Hover Effect',
                defaultValue: false,
              },
              {
                id: ComponentSettingKey.ListDesignItemShowBorder,
                type: BuilderComponentSettingType.Switch,
                label: 'Show Border on List Items',
                defaultValue: false,
              },
              {
                id: ComponentSettingKey.ListDesignSameHeight,
                label: 'Force Equal Height',
                type: BuilderComponentSettingType.Switch,
                help: 'Only works when list layout display is column',
                defaultValue: true,
              },
            ],
          },
          [LABELS.TYPOGRAPHY_KEY]: makeTypographyGroup(ComponentSettingKey.ListDesignTypography, [
            {
              id: ComponentSettingKey.ListDesignLabelTypography,
              label: LABELS.LINE1_KEY,
              defaultValue: 'Label Medium',
            },
            {
              id: ComponentSettingKey.ListDesignSupportingTextTypography,
              label: LABELS.LINE2_KEY,
              defaultValue: 'Body Medium',
            },
            {
              id: ComponentSettingKey.ListDesignText3Typography,
              label: LABELS.LINE3_KEY,
              defaultValue: 'Label Small',
            },
            {
              id: ComponentSettingKey.ListDesignMetadataTypography,
              label: LABELS.METADATA_KEY,
              defaultValue: 'Label Small',
            },
            {
              id: ComponentSettingKey.ListDesignHeadlineTypography,
              label: LABELS.COMPONENT_TITLE_KEY,
              defaultValue: 'Label Large',
            },
            {
              id: ComponentSettingKey.ListDesignGroupHeaderTypography,
              label: LABELS.GROUP_HEADER_KEY,
              defaultValue: 'Display Small',
            },
          ]),
          [LABELS.SPACING_KEY]: {
            items: [
              {
                id: ComponentSettingKey.ListDesignMargin,
                type: BuilderComponentSettingType.Spacing,
                label: 'Component Margin',
                defaultValue: {
                  top: {
                    value: 10,
                    unit: SpacingInputUnit.Pixel,
                  },
                  bottom: {
                    value: 10,
                    unit: SpacingInputUnit.Pixel,
                  },
                  left: {
                    value: 10,
                    unit: SpacingInputUnit.Pixel,
                  },
                  right: {
                    value: 10,
                    unit: SpacingInputUnit.Pixel,
                  },
                },
              },
              // {
              //   id: ComponentSettingKey.ListDesignColumnMargin,
              //   type: BuilderComponentSettingType.Spacing,
              //   label: 'Column Margin',
              // },
              {
                type: BuilderComponentSettingType.SpacingSingle,
                label: 'Item Gap',
                id: ComponentSettingKey.ListDesignItemGap,
                defaultValue: {
                  value: 0,
                  unit: SpacingInputUnit.Pixel,
                },
              },
              // {
              //   id: ComponentSettingKey.ListDesignItemMargin,
              //   type: BuilderComponentSettingType.Spacing,
              //   label: 'Item Margin',
              // },
              {
                id: ComponentSettingKey.ListDesignItemPadding,
                type: BuilderComponentSettingType.Spacing,
                label: 'Item Padding',
              },
              {
                id: ComponentSettingKey.ListDesignHeadlineMargin,
                type: BuilderComponentSettingType.Spacing,
                label: LABELS.COMPONENT_TITLE_MARGIN_KEY,
              },
              {
                id: ComponentSettingKey.ListDesignGroupHeaderMargin,
                type: BuilderComponentSettingType.Spacing,
                label: LABELS.GROUP_HEADER_MARGIN_KEY,
              },
              {
                id: ComponentSettingKey.ListDesignSearchMargin,
                type: BuilderComponentSettingType.Spacing,
                label: LABELS.SEARCH_BOX_KEY,
              },
              {
                id: ComponentSettingKey.ListDesignFilterMargin,
                type: BuilderComponentSettingType.Spacing,
                label: LABELS.FILTERS_KEY,
              },
            ],
          },
          [LABELS.TEXT_LINES_KEY]: {
            items: [
              {
                type: BuilderComponentSettingType.Slider,
                label: LABELS.LINE1_KEY,
                id: ComponentSettingKey.ListDesignTitleLines,
                defaultValue: [4],
                sliderSettings: {
                  max: 4,
                  min: 1,
                  step: 1,
                  range: false,
                  unit: '',
                  showUnit: false,
                },
              },
              {
                type: BuilderComponentSettingType.Slider,
                label: LABELS.LINE2_KEY,
                id: ComponentSettingKey.ListDesignSupportingTextLines,
                defaultValue: [4],
                sliderSettings: {
                  max: 4,
                  min: 1,
                  step: 1,
                  range: false,
                  unit: '',
                  showUnit: false,
                },
              },
              {
                type: BuilderComponentSettingType.Slider,
                label: LABELS.LINE3_KEY,
                id: ComponentSettingKey.ListDesignText3Lines,
                defaultValue: [4],
                sliderSettings: {
                  max: 4,
                  min: 1,
                  step: 1,
                  range: false,
                  unit: '',
                  showUnit: false,
                },
              },
            ],
          },
          [LABELS.TEXT_COLORS_OPTION_KEY]: {
            items: [
              {
                id: ComponentSettingKey.ListDesignGroupTitleColor,
                type: BuilderComponentSettingType.Group,
                label: '',
                groupItems: {
                  [LABELS.LINE1_KEY]: [
                    ...makeTextColorSettings(
                      ComponentSettingKey.ListDesignTitleColor,
                      ComponentSettingKey.ListDesignTitleColorSolid,
                      ComponentSettingKey.ListDesignTitleColorStartGradient,
                      ComponentSettingKey.ListDesignTitleColorEndGradient,
                      ComponentSettingKey.ListDesignTitleColorGradientDirection,
                      ComponentSettingKey.ListDesignScheme,
                      'Line 1',
                      'adaptive'
                    ).items,
                  ],
                  [LABELS.LINE2_KEY]: [
                    ...makeTextColorSettings(
                      ComponentSettingKey.ListDesignSupportingTextColor,
                      ComponentSettingKey.ListDesignSupportingTextColorSolid,
                      ComponentSettingKey.ListDesignSupportingTextColorStartGradient,
                      ComponentSettingKey.ListDesignSupportingTextColorEndGradient,
                      ComponentSettingKey.ListDesignSupportingTextColorGradientDirection,
                      ComponentSettingKey.ListDesignScheme,
                      'Line 2',
                      'adaptive'
                    ).items,
                  ],
                  [LABELS.LINE3_KEY]: [
                    ...makeTextColorSettings(
                      ComponentSettingKey.ListDesignText3Color,
                      ComponentSettingKey.ListDesignText3ColorSolid,
                      ComponentSettingKey.ListDesignText3ColorStartGradient,
                      ComponentSettingKey.ListDesignText3ColorEndGradient,
                      ComponentSettingKey.ListDesignText3ColorGradientDirection,
                      ComponentSettingKey.ListDesignScheme,
                      'Line 3',
                      'adaptive'
                    ).items,
                  ],
                  [LABELS.COMPONENT_TITLE_KEY]: [
                    ...makeTextColorSettings(
                      ComponentSettingKey.ListDesignCompTitleColor,
                      ComponentSettingKey.ListDesignCompTitleColorSolid,
                      ComponentSettingKey.ListDesignCompTitleColorStartGradient,
                      ComponentSettingKey.ListDesignCompTitleColorEndGradient,
                      ComponentSettingKey.ListDesignCompTitleColorGradientDirection,
                      ComponentSettingKey.ListDesignScheme,
                      'Component Title',
                      'adaptive'
                    ).items,
                  ],
                  [LABELS.GROUP_HEADER_KEY]: [
                    ...makeTextColorSettings(
                      ComponentSettingKey.ListDesignGroupHeaderColor,
                      ComponentSettingKey.ListDesignGroupHeaderColorSolid,
                      ComponentSettingKey.ListDesignGroupHeaderColorStartGradient,
                      ComponentSettingKey.ListDesignGroupHeaderColorEndGradient,
                      ComponentSettingKey.ListDesignGroupHeaderColorGradientDirection,
                      ComponentSettingKey.ListDesignScheme,
                      'Group Header',
                      'adaptive'
                    ).items,
                  ],
                },
              },
            ],
          },
          [LABELS.HOVER_EFFECT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.ListDesignHoverEffectVariant,
                type: BuilderComponentSettingType.Select,
                label: LABELS.HOVER_EFFECT_KEY,
                selectSettings: {
                  options: hoverEffectOptions,
                },
              },
            ],
          },
        },
        action: [
          {
            id: ComponentSettingKey.ListActionItemClick,
            label: LABELS.ON_ITEM_CLICK_KEY,
            isSimple: false,
            defaultValue: [
              {
                type: IActionType.ShowDetailsScreen,
                settings: {
                  overlaySettings: {
                    closeBehavior: 'direct',
                    opacity: 85,
                    placement: {
                      desktop: 'left',
                      phone: 'bottom',
                      'phone-landscape': 'left',
                      tablet: 'bottom',
                      'tablet-landscape': 'left',
                      tv: 'left',
                    },
                    shape: {
                      desktop: 'medium',
                      phone: 'medium',
                      'phone-landscape': 'medium',
                      tablet: 'medium',
                      'tablet-landscape': 'medium',
                      tv: 'medium',
                    },
                    size: {
                      desktop: '40%, 100%',
                      phone: '90%, 100%',
                      'phone-landscape': '50%, 100%',
                      tablet: '90%, 100%',
                      'tablet-landscape': '50%, 100%',
                      tv: '40%, 100%',
                    },
                  },
                  screenType: 'overlay',
                },
              },
            ],
            actionTypes: [IActionType.ShowDetailsScreen],
          },
          {
            id: ComponentSettingKey.ListActionItemSelection,
            label: 'On Item Select',
            isSimple: true,
            showLabel: true,
            defaultValue: {
              type: IActionType.ShowContextualMenu,
              settings: {},
            },
            dataSource: ComponentSettingKey.ListDataSource,
            actionTypes: [IActionType.ShowContextualMenu],
          },
          {
            id: ComponentSettingKey.ListActionIconButtonClick,
            label: 'On Icon Button Click',
            isSimple: false,
            showLabel: true,
            defaultValue: [
              {
                type: IActionType.ShowDetailsScreen,
                settings: {},
              },
            ],
            dataSource: ComponentSettingKey.ListDataSource,
            actionTypes: [IActionType.ShowContextualMenu],
          },
          {
            id: ComponentSettingKey.ListActionAddButtonClick,
            label: 'On Add Button Click',
            isSimple: false,
            showLabel: true,
            defaultValue: [
              {
                type: IActionType.FormAddItemScreen,
                settings: {
                  screenType: 'overlay',
                },
              },
            ],
            dataSource: ComponentSettingKey.ListDataSource,
            actionTypes: [IActionType.FormAddItemScreen],
          },
        ],
        behavior: {
          custom: {
            [LABELS.APP_FILTERS_KEY]: {
              items: [
                {
                  id: ComponentSettingKey.ListBehaviorUserFilterPosition,
                  type: BuilderComponentSettingType.Select,
                  label: 'Filter Position',
                  defaultValue: 'top',
                  forceHorizontal: true,
                  selectSettings: {
                    options: [
                      {
                        value: 'inline',
                        label: 'Inline',
                      },
                      {
                        value: 'top',
                        label: 'Top',
                      },
                    ],
                  },
                },
                {
                  id: ComponentSettingKey.ListBehaviorUserFilter,
                  label: '',
                  type: BuilderComponentSettingType.UserColumnFilter,
                  columnFilterSettings: {
                    tableSourceId: ComponentSettingKey.ListDataSource,
                  },
                },
              ],
            },
            [LABELS.USER_SEARCH_KEY]: {
              items: [
                {
                  id: ComponentSettingKey.ListBehaviorUserSearch,
                  type: BuilderComponentSettingType.Switch,
                  defaultValue: true,
                  label: LABELS.ENABLE_USER_SEARCH,
                },
                {
                  id: ComponentSettingKey.ListBehaviorSearchResultsInfo,
                  type: BuilderComponentSettingType.Switch,
                  defaultValue: true,
                  label: LABELS.SHOW_SEARCH_RESULTS_INFO_KEY,
                },
              ],
            },
            [LABELS.ADD_BUTTON_KEY]: {
              items: [
                {
                  id: ComponentSettingKey.ListBehaviorAddButtonShow,
                  type: BuilderComponentSettingType.Switch,
                  label: 'Show',
                  defaultValue: true,
                },
                {
                  id: ComponentSettingKey.ListBehaviorAddButtonPosition,
                  type: BuilderComponentSettingType.Select,
                  label: 'Button Position',
                  defaultValue: 'inline',
                  forceHorizontal: true,
                  selectSettings: {
                    options: [
                      {
                        value: 'inline',
                        label: 'Inline',
                      },
                      {
                        value: 'bottom',
                        label: 'Bottom',
                      },
                    ],
                  },
                },
                {
                  id: ComponentSettingKey.ListBehaviorAddButtonVisibility,
                  type: BuilderComponentSettingType.Visibility,
                  label: 'Show Add Button',
                  defaultValue: {
                    enabled: true,
                    conditions: [
                      {
                        lhs: {
                          col: 'PROFILE_ROLE',
                          flag: ConditionFeatureFlag.ProfileData,
                        },
                        operator: 'text-is-equal-to',
                        rhs: {
                          flag: 4,
                          value: 'admin',
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          visibility: true,
        },
      },
    },

    // card
    {
      compId: 'card',
      title: 'Cards',
      searchKeywords: [
        'cards',
        'product card',
        'item card',
        'tile',
        'block',
        'preview card',
        'profile card',
        'content card',
      ],
      icon: 'Card24',
      nested: false,
      //detailDataSource: ComponentSettingKey.CardDataSource,
      settings: {
        dataGroup: {
          [LABELS.DATA_SOURCE_KEY]: {
            items: [
              makeDataSource(ComponentSettingKey.CardDataSource),
              {
                id: ComponentSettingKey.CardDataThumbnail,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.THUMBNAIL_CARD_KEY,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.CardDataSource,
                  columnTypes: [ColumnTypes.Image, ColumnTypes.Icon, ColumnTypes.Lookup],
                  noAutoSelect: false,
                  includeLookupType: true,
                },
              },
              {
                id: ComponentSettingKey.CardDataHeader,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.LINE1_KEY,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.CardDataSource,
                  columnTypes: withoutSlugColumnTypes,
                  includeLookupType: true,
                },
              },
              {
                id: ComponentSettingKey.CardDataSubHeader,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.LINE2_KEY,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.CardDataSource,
                  columnTypes: withoutSlugColumnTypes,
                  noAutoSelect: true,
                  includeLookupType: true,
                },
              },
              {
                id: ComponentSettingKey.CardDataMedia,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.MEDIA_CARD_KEY,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.CardDataSource,
                  columnTypes: [
                    ColumnTypes.Image,
                    ColumnTypes.URL,
                    ColumnTypes.Attachment,
                    ColumnTypes.ImageArray,
                    ColumnTypes.Icon,
                    ColumnTypes.Lookup,
                    ColumnTypes.Barcode,
                    ColumnTypes.QRCode,
                  ],
                  includeLookupType: true,
                },
              },
              {
                id: ComponentSettingKey.CardDataTitle,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.LINE3_KEY,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.CardDataSource,
                  columnTypes: withoutSlugColumnTypes,
                  includeLookupType: true,
                  noAutoSelect: true,
                },
              },
              {
                id: ComponentSettingKey.CardDataSubtitle,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.LINE4_KEY,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.CardDataSource,
                  columnTypes: withoutSlugColumnTypes,
                  includeLookupType: true,
                  noAutoSelect: true,
                },
              },
              {
                id: ComponentSettingKey.CardDataSupportingText,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.LINE5_KEY,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.CardDataSource,
                  columnTypes: withoutSlugColumnTypes,
                  includeLookupType: true,
                  noAutoSelect: true,
                },
              },
            ],
          },
          [LABELS.METADATA_KEY]: {
            items: [
              {
                id: ComponentSettingKey.CardDataMetadata1,
                type: BuilderComponentSettingType.VariableSelect,
                label: LABELS.METADATA_KEY,
                variableSelectSettings: {
                  multiple: true,
                  flags:
                    VariableSelectFeatureFlag.ProfileData |
                    VariableSelectFeatureFlag.SingleCell |
                    // VariableSelectFeatureFlag.CurrentRow |
                    VariableSelectFeatureFlag.CurrentListItem |
                    VariableSelectFeatureFlag.Icon |
                    VariableSelectFeatureFlag.Space |
                    VariableSelectFeatureFlag.CustomText,
                  tableSourceId: ComponentSettingKey.CardDataSource,
                  columnTypes: [
                    ColumnTypes.Text,
                    ColumnTypes.Address,
                    ColumnTypes.Number,
                    ColumnTypes.DateTime,
                    ColumnTypes.Icon,
                    ColumnTypes.Rating,
                    ColumnTypes.URL,
                    ColumnTypes.Join,
                    ColumnTypes.Phone,
                    ColumnTypes.Email,
                    ColumnTypes.Template,
                    ColumnTypes.Lookup,
                  ],
                  // Custom order untuk menampilkan opsi sesuai urutan yang diinginkan
                  flagOrder: [
                    VariableSelectFeatureFlag.CurrentListItem,
                    VariableSelectFeatureFlag.Icon,
                    VariableSelectFeatureFlag.ProfileData,
                    VariableSelectFeatureFlag.SingleCell,
                    VariableSelectFeatureFlag.Space,
                    VariableSelectFeatureFlag.GlobalVariable,
                    VariableSelectFeatureFlag.CustomText,
                  ],
                },
              },
            ],
          },
          [LABELS.HEADER_ELEMENTS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.CardDataTrailingHeaderMenu,
                label: 'Header Menu',
                help: 'Select menu created in Menu Tab',
                type: BuilderComponentSettingType.MenuSelect,
                menuSelectSettings: {},
              },
            ],
          },
          [LABELS.FOOTER_ELEMENTS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.CardDataFooter,
                type: BuilderComponentSettingType.Group,
                label: '',
                groupItems: {
                  [LABELS.FOOTER_TITLE_KEY]: [
                    {
                      id: ComponentSettingKey.CardDataActionHelp,
                      type: BuilderComponentSettingType.Text,
                      label: '',
                    },
                  ],
                  [LABELS.LEADING_BUTTON_KEY]: [
                    {
                      id: ComponentSettingKey.CardDataLeadingAction,
                      type: BuilderComponentSettingType.Switch,
                      label: LABELS.ENABLE_KEY,
                      defaultValue: true,
                    },
                    {
                      id: ComponentSettingKey.CardDataLeadingActionIcon,
                      type: BuilderComponentSettingType.Icon,
                      label: LABELS.ICON_KEY,
                      defaultValue: 'arrow_forward',
                      dependOn: {
                        id: ComponentSettingKey.CardDataLeadingAction,
                        showWhen: (v) => v === true,
                      },
                    },
                    {
                      id: ComponentSettingKey.CardDataLeadingActionIconPosition,
                      type: BuilderComponentSettingType.Select,
                      label: LABELS.ICON_POSITION_KEY,
                      defaultValue: LABELS.LEADING_KEY,
                      forceHorizontal: true,
                      selectSettings: {
                        options: [
                          {
                            value: 'leading',
                            label: 'Leading',
                          },
                          {
                            value: 'trailing',
                            label: 'Trailing',
                          },
                        ],
                      },
                      dependOn: {
                        id: ComponentSettingKey.CardDataLeadingAction,
                        showWhen: (v) => v === true,
                      },
                    },
                    {
                      id: ComponentSettingKey.CardDataLeadingActionLabel,
                      type: BuilderComponentSettingType.VariableText,
                      label: LABELS.LABEL_KEY,
                      defaultValue: [
                        {
                          type: 'paragraph',
                          children: [{ text: 'Learn More' }],
                        },
                      ],
                      variableTextSettings: {
                        flags:
                          VariableTextFeatureFlag.ColumnData |
                          VariableTextFeatureFlag.ProfileData |
                          VariableTextFeatureFlag.CurrentRow |
                          VariableTextFeatureFlag.SingleCell |
                          VariableTextFeatureFlag.CurrentListItem,
                        tableSourceId: ComponentSettingKey.CardDataSource,
                      },
                      dependOn: {
                        id: ComponentSettingKey.CardDataLeadingAction,
                        showWhen: (v) => v === true,
                      },
                    },
                  ],
                  'Trailing Button': [
                    {
                      id: ComponentSettingKey.CardDataTrailingAction,
                      type: BuilderComponentSettingType.Switch,
                      label: LABELS.ENABLE_KEY,
                      defaultValue: true,
                    },
                    {
                      id: ComponentSettingKey.CardDataTrailingActionIcon,
                      type: BuilderComponentSettingType.Icon,
                      label: LABELS.ICON_KEY,
                      defaultValue: '',
                      dependOn: {
                        id: ComponentSettingKey.CardDataTrailingAction,
                        showWhen: (v) => v === true,
                      },
                    },
                    {
                      id: ComponentSettingKey.CardDataTrailingActionIconPosition,
                      type: BuilderComponentSettingType.Select,
                      label: LABELS.ICON_POSITION_KEY,
                      defaultValue: 'leading',
                      selectSettings: {
                        options: [
                          {
                            value: 'leading',
                            label: 'Leading',
                          },
                          {
                            value: 'trailing',
                            label: 'Trailing',
                          },
                        ],
                      },
                      dependOn: {
                        id: ComponentSettingKey.CardDataTrailingAction,
                        showWhen: (v) => v === true,
                      },
                    },
                    {
                      id: ComponentSettingKey.CardDataTrailingActionLabel,
                      type: BuilderComponentSettingType.VariableText,
                      label: LABELS.LABEL_KEY,
                      defaultValue: [
                        {
                          type: 'paragraph',
                          children: [{ text: 'Message' }],
                        },
                      ],
                      variableTextSettings: {
                        flags:
                          VariableTextFeatureFlag.ColumnData |
                          VariableTextFeatureFlag.ProfileData |
                          VariableTextFeatureFlag.CurrentRow |
                          VariableTextFeatureFlag.SingleCell |
                          VariableTextFeatureFlag.CurrentListItem,
                        tableSourceId: ComponentSettingKey.CardDataSource,
                      },
                      dependOn: {
                        id: ComponentSettingKey.CardDataTrailingAction,
                        showWhen: (v) => v === true,
                      },
                    },
                  ],
                  [LABELS.TRAILING_MENU_KEY]: [
                    {
                      id: ComponentSettingKey.CardDataTrailingMenu,
                      label: '',
                      help: LABELS.SELECT_MENU_KEY,
                      type: BuilderComponentSettingType.MenuSelect,
                    },
                  ],
                },
              },
            ],
          },
          [LABELS.COMPONENT_TITLE_KEY]: {
            items: [
              {
                id: ComponentSettingKey.CardDataCompTitle,
                type: BuilderComponentSettingType.VariableText,
                label: '',
                defaultValue: [
                  {
                    type: 'paragraph',
                    children: [{ text: '' }],
                  },
                ],
                variableTextSettings: {
                  flags:
                    VARIABLE_TEXT_FEATURE_FLAG_ALL &
                    ~VariableTextFeatureFlag.ColumnData &
                    ~VariableTextFeatureFlag.Image,
                },
              },
            ],
          },
          [LABELS.SORTING_KEY]: makeSorting(
            ComponentSettingKey.CardDataSortBy,
            ComponentSettingKey.CardDataSortType,
            ComponentSettingKey.CardDataSource
          ),
          [LABELS.DATA_FILTER_KEY]: {
            items: [
              {
                id: ComponentSettingKey.CardDataFilter,
                label: LABELS.FILTER_KEY,
                type: BuilderComponentSettingType.ColumnFilter,
                columnFilterSettings: {
                  tableSourceId: ComponentSettingKey.CardDataSource,
                },
              },
            ],
          },
          [LABELS.GROUPING_KEY]: {
            items: [
              {
                id: ComponentSettingKey.CardDataGroupBy,
                label: LABELS.GROUP_BY_KEY,
                help: 'Only works when in vertical layout mode',
                type: BuilderComponentSettingType.ColumnSelect,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.CardDataSource,
                  noAutoSelect: true,
                  includeLookupType: true,
                },
              },
            ],
          },
        },
        designGroup: {
          [LABELS.COLORS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.CardDesignColorMode,
                label: 'Color Mode',
                type: BuilderComponentSettingType.Select,
                selectSettings: {
                  options: [
                    { value: 'static', label: 'Static' },
                    { value: 'random', label: 'Random' },
                    { value: 'sequential', label: 'Sequential' },
                  ],
                },
                defaultValue: 'static',
              },
              {
                id: ComponentSettingKey.CardDesignColorToneOrder,
                label: 'Color Tone Order',
                type: BuilderComponentSettingType.IconSelect,
                iconSelectSettings: {
                  options: [
                    { id: 'asc', title: 'Ascending', icon: 'Asc16' },
                    { id: 'desc', title: 'Descending', icon: 'Desc16' },
                  ],
                },
                defaultValue: 'asc',
                dependOn: {
                  id: ComponentSettingKey.CardDesignColorMode,
                  showWhen: (v) => v === 'sequential',
                },
              },
              {
                id: ComponentSettingKey.CardDesignColorStartPalette,
                label: 'Color Start Palette',
                type: BuilderComponentSettingType.Select,
                selectSettings: {
                  options: [
                    { value: 'primary', label: 'Primary' },
                    { value: 'secondary', label: 'Secondary' },
                    { value: 'tertiary', label: 'Tertiary' },
                  ],
                },
                dependOn: {
                  id: ComponentSettingKey.CardDesignColorMode,
                  showWhen: (v) => v === 'sequential',
                },
              },
              {
                id: ComponentSettingKey.CardDesignBgColor,
                label: 'Card Background',
                defaultValue: 'primary-90',
                type: BuilderComponentSettingType.ColorVariant,
                colorVariantSettings: {
                  fullColor: 'bg',
                  schemeId: ComponentSettingKey.CardDesignScheme,
                },
                dependOn: {
                  id: ComponentSettingKey.CardDesignColorMode,
                  showWhen: (v) => v === 'static',
                },
              },
              {
                id: ComponentSettingKey.CardDesignBgOpacity,
                label: 'Card Background Opacity',
                type: BuilderComponentSettingType.Slider,
                defaultValue: [100],
                sliderSettings: {
                  step: 1,
                  max: 100,
                  min: 0,
                },
                dependOn: {
                  id: ComponentSettingKey.CardDesignColorMode,
                  showWhen: (v) => v === 'static',
                },
              },
              // {
              //   id: ComponentSettingKey.CardDesignHeaderColorVariant,
              //   type: BuilderComponentSettingType.ColorVariant,
              //   label: LABELS.HEADER_KEY,
              //   defaultValue: 'on-primary',
              //   colorVariantSettings: {
              //     fullColor: 'fg',
              //     schemeId: ComponentSettingKey.CardDesignScheme,
              //   },
              // },
              // {
              //   id: ComponentSettingKey.CardDesignSubheaderColorVariant,
              //   type: BuilderComponentSettingType.ColorVariant,
              //   label: 'Subheader',
              //   defaultValue: 'on-primary',
              //   colorVariantSettings: {
              //     fullColor: 'fg',
              //     schemeId: ComponentSettingKey.CardDesignScheme,
              //   },
              // },
              // {
              //   id: ComponentSettingKey.CardDesignTitleColorVariant,
              //   type: BuilderComponentSettingType.ColorVariant,
              //   label: LABELS.TITLE_KEY,
              //   defaultValue: 'on-primary',
              //   colorVariantSettings: {
              //     fullColor: 'fg',
              //     schemeId: ComponentSettingKey.CardDesignScheme,
              //   },
              // },
              // {
              //   id: ComponentSettingKey.CardDesignSubtitleColorVariant,
              //   type: BuilderComponentSettingType.ColorVariant,
              //   label: 'Subtitle',
              //   defaultValue: 'on-primary',
              //   colorVariantSettings: {
              //     fullColor: 'fg',
              //     schemeId: ComponentSettingKey.CardDesignScheme,
              //   },
              // },
              // {
              //   id: ComponentSettingKey.CardDesignSupportingTextColorVariant,
              //   type: BuilderComponentSettingType.ColorVariant,
              //   label: LABELS.SUPPORTING_TEXT_KEY,
              //   defaultValue: 'on-primary',
              //   colorVariantSettings: {
              //     fullColor: 'fg',
              //     schemeId: ComponentSettingKey.CardDesignScheme,
              //   },
              // },
              // {
              //   id: ComponentSettingKey.CardDesignMetadataColorVariant,
              //   type: BuilderComponentSettingType.ColorVariant,
              //   label: LABELS.METADATA_KEY,
              //   defaultValue: 'on-primary',
              //   colorVariantSettings: {
              //     fullColor: 'fg',
              //     schemeId: ComponentSettingKey.CardDesignScheme,
              //   },
              // },
              {
                id: ComponentSettingKey.CardDesignLeadingButtonScheme,
                label: 'Leading Button Color',
                defaultValue: 'primary-20',
                type: BuilderComponentSettingType.ColorVariant,
                colorVariantSettings: {
                  fullColor: 'bg',
                  schemeId: ComponentSettingKey.CardDesignScheme,
                },
              },
              {
                id: ComponentSettingKey.CardDesignTrailingButtonScheme,
                label: 'Trailing Button Color',
                defaultValue: 'secondary-40',
                type: BuilderComponentSettingType.ColorVariant,
                colorVariantSettings: {
                  fullColor: 'bg',
                  schemeId: ComponentSettingKey.CardDesignScheme,
                },
              },
              // {
              //   id: ComponentSettingKey.CardDesignCompTitleColor,
              //   type: BuilderComponentSettingType.ColorVariant,
              //   label: LABELS.COMPONENT_TITLE_KEY,
              //   defaultValue: 'on-primary',
              //   colorVariantSettings: {
              //     fullColor: 'fg',
              //     schemeId: ComponentSettingKey.CardDesignScheme,
              //   },
              // },
              // {
              //   id: ComponentSettingKey.CardDesignGroupHeaderColor,
              //   type: BuilderComponentSettingType.ColorVariant,
              //   label: LABELS.GROUP_HEADER_KEY,
              //   defaultValue: 'on-primary',
              //   colorVariantSettings: {
              //     fullColor: 'fg',
              //     schemeId: ComponentSettingKey.CardDesignScheme,
              //   },
              // },
              {
                id: ComponentSettingKey.CardDesignSearchBgColor,
                type: BuilderComponentSettingType.ColorVariant,
                label: 'Search Box Background',
                defaultValue: 'primary-90',
              },
              {
                id: ComponentSettingKey.CardDesignFilterChipColor,
                type: BuilderComponentSettingType.ColorVariant,
                label: LABELS.FILTERS_CHIP_KEY,
                defaultValue: 'primary-95',
              },
              {
                id: ComponentSettingKey.CardDesignFilterBgColor,
                type: BuilderComponentSettingType.ColorVariant,
                label: LABELS.FILTERS_BG_KEY,
                defaultValue: 'primary-95',
              },
              {
                id: ComponentSettingKey.CardDesignAddButtonColor,
                type: BuilderComponentSettingType.ColorVariant,
                label: 'Add Button Color',
                defaultValue: 'primary-20',
              },
            ],
          },
          [LABELS.TEXT_COLORS_OPTION_KEY]: {
            items: [
              {
                id: ComponentSettingKey.CardDesignGroupTextColor,
                type: BuilderComponentSettingType.Group,
                label: '',
                groupItems: {
                  [LABELS.LINE1_KEY]: [
                    ...makeTextColorSettings(
                      ComponentSettingKey.CardDesignText1Color,
                      ComponentSettingKey.CardDesignText1ColorSolid,
                      ComponentSettingKey.CardDesignText1ColorStartGradient,
                      ComponentSettingKey.CardDesignText1ColorEndGradient,
                      ComponentSettingKey.CardDesignText1ColorGradientDirection,
                      ComponentSettingKey.CardDesignScheme,
                      LABELS.LINE1_KEY,
                      'adaptive'
                    ).items,
                  ],
                  [LABELS.LINE2_KEY]: [
                    ...makeTextColorSettings(
                      ComponentSettingKey.CardDesignText2Color,
                      ComponentSettingKey.CardDesignText2ColorSolid,
                      ComponentSettingKey.CardDesignText2ColorStartGradient,
                      ComponentSettingKey.CardDesignText2ColorEndGradient,
                      ComponentSettingKey.CardDesignText2ColorGradientDirection,
                      ComponentSettingKey.CardDesignScheme,
                      LABELS.LINE2_KEY,
                      'adaptive'
                    ).items,
                  ],
                  [LABELS.LINE3_KEY]: [
                    ...makeTextColorSettings(
                      ComponentSettingKey.CardDesignText3Color,
                      ComponentSettingKey.CardDesignText3ColorSolid,
                      ComponentSettingKey.CardDesignText3ColorStartGradient,
                      ComponentSettingKey.CardDesignText3ColorEndGradient,
                      ComponentSettingKey.CardDesignText3ColorGradientDirection,
                      ComponentSettingKey.CardDesignScheme,
                      LABELS.LINE3_KEY,
                      'solid',
                      {
                        defaultSolidColor: 'primary-30',
                      }
                    ).items,
                  ],
                  [LABELS.LINE4_KEY]: [
                    ...makeTextColorSettings(
                      ComponentSettingKey.CardDesignText4Color,
                      ComponentSettingKey.CardDesignText4ColorSolid,
                      ComponentSettingKey.CardDesignText4ColorStartGradient,
                      ComponentSettingKey.CardDesignText4ColorEndGradient,
                      ComponentSettingKey.CardDesignText4ColorGradientDirection,
                      ComponentSettingKey.CardDesignScheme,
                      LABELS.LINE4_KEY,
                      'adaptive'
                    ).items,
                  ],
                  [LABELS.LINE5_KEY]: [
                    ...makeTextColorSettings(
                      ComponentSettingKey.CardDesignText5Color,
                      ComponentSettingKey.CardDesignText5ColorSolid,
                      ComponentSettingKey.CardDesignText5ColorStartGradient,
                      ComponentSettingKey.CardDesignText5ColorEndGradient,
                      ComponentSettingKey.CardDesignText5ColorGradientDirection,
                      ComponentSettingKey.CardDesignScheme,
                      LABELS.LINE5_KEY,
                      'adaptive'
                    ).items,
                  ],
                  [LABELS.METADATA_KEY]: [
                    ...makeTextColorSettings(
                      ComponentSettingKey.CardDesignMetadataColor,
                      ComponentSettingKey.CardDesignMetadataColorSolid,
                      ComponentSettingKey.CardDesignMetadataColorStartGradient,
                      ComponentSettingKey.CardDesignMetadataColorEndGradient,
                      ComponentSettingKey.CardDesignMetadataColorGradientDirection,
                      ComponentSettingKey.CardDesignScheme,
                      LABELS.METADATA_KEY,
                      'adaptive'
                    ).items,
                  ],
                  [LABELS.FOOTER_LABEL_KEY]: [
                    ...makeTextColorSettings(
                      ComponentSettingKey.CardDesignFooterAreaColor,
                      ComponentSettingKey.CardDesignFooterAreaColorSolid,
                      ComponentSettingKey.CardDesignFooterAreaColorStartGradient,
                      ComponentSettingKey.CardDesignFooterAreaColorEndGradient,
                      ComponentSettingKey.CardDesignFooterAreaColorGradientDirection,
                      ComponentSettingKey.CardDesignScheme,
                      LABELS.FOOTER_LABEL_KEY,
                      'adaptive'
                    ).items,
                  ],
                  [LABELS.GROUP_HEADER_KEY]: [
                    ...makeTextColorSettings(
                      ComponentSettingKey.CardDesignGroupHeaderColor,
                      ComponentSettingKey.CardDesignGroupHeaderColorSolid,
                      ComponentSettingKey.CardDesignGroupHeaderColorStartGradient,
                      ComponentSettingKey.CardDesignGroupHeaderColorEndGradient,
                      ComponentSettingKey.CardDesignGroupHeaderColorGradientDirection,
                      ComponentSettingKey.CardDesignScheme,
                      LABELS.GROUP_HEADER_KEY,
                      'adaptive'
                    ).items,
                  ],
                  [LABELS.COMPONENT_TITLE_KEY]: [
                    ...makeTextColorSettings(
                      ComponentSettingKey.CardDesignCompTitleColor,
                      ComponentSettingKey.CardDesignCompTitleColorSolid,
                      ComponentSettingKey.CardDesignCompTitleColorStartGradient,
                      ComponentSettingKey.CardDesignCompTitleColorEndGradient,
                      ComponentSettingKey.CardDesignCompTitleColorGradientDirection,
                      ComponentSettingKey.CardDesignScheme,
                      LABELS.COMPONENT_TITLE_KEY,
                      'adaptive'
                    ).items,
                  ],
                },
              },
            ],
          },
          // [LABELS.VARIANTS_KEY]: {
          //   items: [
          //     {
          //       id: ComponentSettingKey.CardDesignVariant,
          //       defaultValue: CardVariant.Elevated,
          //       label: LABELS.CARD_KEY,
          //       type: BuilderComponentSettingType.Select,
          //       selectSettings: {
          //         options: Object.entries(CardVariant).map(([k, v]) => {
          //           return {
          //             value: v,
          //             label: k,
          //           }
          //         }),
          //       },
          //     },
          //     {
          //       id: ComponentSettingKey.CardDesignOutlinedNoBorder,
          //       type: BuilderComponentSettingType.Switch,
          //       label: 'Remove Border',
          //       dependOn: {
          //         id: ComponentSettingKey.CardDesignVariant,
          //         showWhen: (v) => v === CardVariant.Outlined,
          //       },
          //     },
          //     {
          //       id: ComponentSettingKey.CardDesignOutlinedNoHoverBg,
          //       type: BuilderComponentSettingType.Switch,
          //       label: 'Remove Background on Hover',
          //       dependOn: {
          //         id: ComponentSettingKey.CardDesignVariant,
          //         showWhen: (v) => v === CardVariant.Outlined,
          //       },
          //     },
          //     {
          //       id: ComponentSettingKey.CardDesignOutlinedNoPadding,
          //       type: BuilderComponentSettingType.Switch,
          //       label: 'Remove Padding',
          //       dependOn: {
          //         id: ComponentSettingKey.CardDesignOutlinedNoBorder,
          //         showWhen: (v, elId, ancestors, ctx) => {
          //           const isNoBorder = v === true
          //           const isOutlined =
          //             (ctx?.page?.elementSettings[elId ?? '']?.[ComponentSettingKey.CardDesignVariant] as string) ===
          //             CardVariant.Outlined
          //           return isNoBorder && isOutlined
          //         },
          //       },
          //     },
          //     {
          //       id: ComponentSettingKey.CardDesignLeadingButtonVariant,
          //       type: BuilderComponentSettingType.Select,
          //       label: 'Leading Button',
          //       defaultValue: MdButtonVariant.Outlined,
          //       selectSettings: {
          //         options: Object.entries(MdButtonVariant).map(([label, value]) => ({
          //           label,
          //           value,
          //         })),
          //       },
          //     },
          //     {
          //       id: ComponentSettingKey.CardDesignTrailingButtonVariant,
          //       type: BuilderComponentSettingType.Select,
          //       label: 'Trailing Button',
          //       defaultValue: MdButtonVariant.Filled,
          //       selectSettings: {
          //         options: Object.entries(MdButtonVariant).map(([label, value]) => ({
          //           label,
          //           value,
          //         })),
          //       },
          //     },
          //   ],
          // },
          [LABELS.DIMENSION_KEY]: {
            items: [
              {
                id: ComponentSettingKey.CardDesignWidthGroup,
                type: BuilderComponentSettingType.Group,
                label: '',
                groupItems: {
                  'Cards & Search': [
                    ...makeClampWidthItems(ComponentSettingKey.CardDesignWidth, {
                      label: LABELS.CARD_KEY,
                    }).items,
                    ...makeClampWidthItems(ComponentSettingKey.CardDesignSearchBox, {
                      label: LABELS.SEARCH_BOX_KEY,
                    }).items,
                  ],
                  Buttons: [
                    {
                      type: BuilderComponentSettingType.Switch,
                      label: 'Leading Button Full Width',
                      id: ComponentSettingKey.CardDesignLeadingButtonFullWidth,
                      defaultValue: true,
                    },
                    {
                      type: BuilderComponentSettingType.Switch,
                      label: 'Trailing Button Full Width',
                      id: ComponentSettingKey.CardDesignTrailingButtonFullWidth,
                      defaultValue: true,
                    },
                  ],
                  [LABELS.MEDIA_ICON_KEY]: [
                    {
                      id: ComponentSettingKey.CardDesignMediaIconSize,
                      type: BuilderComponentSettingType.Slider,
                      defaultValue: [48],
                      label: 'Media Icon',
                      sliderSettings: {
                        max: 150,
                        min: 18,
                        range: false,
                        step: 1,
                      },
                    },
                  ],
                },
              },
            ],
          },
          [LABELS.CORNER_RADIUS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.CardDesignShape,
                label: LABELS.CARD_KEY,
                type: BuilderComponentSettingType.Select,
                defaultValue: MdShape.Medium,
                selectSettings: {
                  options: Object.entries(MdShape)
                    .filter(([_, value]) => {
                      return value !== 'full'
                    })
                    .map(([label, value]) => ({
                      value,
                      label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
                    })),
                },
              },
              {
                id: ComponentSettingKey.CardDesignThumbShape,
                label: LABELS.THUMBNAIL_KEY,
                type: BuilderComponentSettingType.Select,
                defaultValue: MdShape.Full,
                selectSettings: {
                  options: Object.entries(MdShape).map(([label, value]) => ({
                    value,
                    label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
                  })),
                },
              },
              {
                id: ComponentSettingKey.CardDesignMediaShape,
                label: LABELS.MEDIA_KEY,
                type: BuilderComponentSettingType.Select,
                defaultValue: MdShape.Medium,
                selectSettings: {
                  options: Object.entries(MdShape).map(([label, value]) => ({
                    value,
                    label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
                  })),
                },
              },
              {
                id: ComponentSettingKey.CardDesignLeadingButtonShape,
                label: LABELS.LEADING_ACTION_BUTTON_KEY,
                type: BuilderComponentSettingType.Select,
                defaultValue: MdShape.Medium,
                selectSettings: {
                  options: Object.entries(MdShape).map(([label, value]) => ({
                    value,
                    label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
                  })),
                },
              },
              {
                id: ComponentSettingKey.CardDesignTrailingButtonShape,
                label: LABELS.TRAILING_ACTION_BUTTON_KEY,
                type: BuilderComponentSettingType.Select,
                defaultValue: MdShape.Medium,
                selectSettings: {
                  options: Object.entries(MdShape).map(([label, value]) => ({
                    value,
                    label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
                  })),
                },
              },
              {
                id: ComponentSettingKey.CardDesignSearchCorner,
                label: LABELS.SEARCH_BOX_KEY,
                type: BuilderComponentSettingType.Select,
                defaultValue: MdShape.Medium,
                selectSettings: {
                  options: Object.entries(MdShape).map(([label, value]) => ({
                    value,
                    label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
                  })),
                },
              },
              {
                id: ComponentSettingKey.CardDesignFilterCornerRadius,
                label: LABELS.FILTERS_CHIP_KEY,
                type: BuilderComponentSettingType.Select,
                defaultValue: MdShape.Medium,
                selectSettings: {
                  options: Object.entries(MdShape).map(([label, value]) => ({
                    value,
                    label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
                  })),
                },
              },
              {
                id: ComponentSettingKey.CardDesignAddButtonCornerRadius,
                label: 'Add Button',
                type: BuilderComponentSettingType.Select,
                defaultValue: MdShape.Medium,
                selectSettings: {
                  options: Object.entries(MdShape).map(([label, value]) => ({
                    value,
                    label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
                  })),
                },
              },
            ],
          },
          [LABELS.THUMBNAIL_ICON_KEY]: {
            items: [
              {
                id: ComponentSettingKey.CardDesignIconVariant,
                type: BuilderComponentSettingType.Select,
                label: 'Variant',
                defaultValue: 'filled',
                selectSettings: {
                  options: Object.entries(MdIconButtonVariant)
                    .filter(
                      ([k, v]) =>
                        v !== MdIconButtonVariant.Elevated &&
                        v !== MdIconButtonVariant.FilledTonal &&
                        v !== MdIconButtonVariant.Outlined
                    )
                    .map(([label, value]) => ({
                      label,
                      value,
                    })),
                },
              },
              {
                id: ComponentSettingKey.CardDesignIconBackgroundColorVariant,
                type: BuilderComponentSettingType.ColorVariant,
                label: LABELS.ICON_BACKGROUND_COLOR_KEY,
                defaultValue: 'primary-80',
                colorVariantSettings: {
                  fullColor: 'bg',
                  schemeId: ComponentSettingKey.CardDesignScheme,
                },
                dependOn: {
                  id: ComponentSettingKey.CardDesignIconVariant,
                  showWhen: (v) => v === 'filled' || v === 'outlined',
                },
              },
              {
                id: ComponentSettingKey.CardDesignIconColorVariant,
                type: BuilderComponentSettingType.ColorVariant,
                label: 'Thumbnail Icon',
                defaultValue: 'primary-20',
                colorVariantSettings: {
                  fullColor: 'bg',
                  schemeId: ComponentSettingKey.CardDesignScheme,
                },
              },
              {
                id: ComponentSettingKey.CardDesignIconColorCustom,
                type: BuilderComponentSettingType.Switch,
                label: 'Dynamic Color',
                defaultValue: false,
              },
              {
                id: ComponentSettingKey.CardDesignIconBackgroundColorColumn,
                type: BuilderComponentSettingType.ColumnSelect,
                label: 'Select Background Color Column',
                help: 'If no color found will fallback to the color variant settings.',
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.CardDataSource,
                  columnTypes: [ColumnTypes.Color],
                },
                dependOn: {
                  id: ComponentSettingKey.CardDesignIconColorCustom,
                  showWhen: (v) => v === true,
                },
              },
              {
                id: ComponentSettingKey.CardDesignIconColorColumn,
                type: BuilderComponentSettingType.ColumnSelect,
                label: 'Select Color Column',
                help: 'If no color found will fallback to the color variant settings.',
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.CardDataSource,
                  columnTypes: [ColumnTypes.Color],
                },
                dependOn: {
                  id: ComponentSettingKey.CardDesignIconColorCustom,
                  showWhen: (v) => v === true,
                },
              },
            ],
          },
          [LABELS.MEDIA_ICON_KEY]: {
            items: [
              {
                id: ComponentSettingKey.CardDesignMediaIconVariant,
                type: BuilderComponentSettingType.Select,
                label: 'Variant',
                defaultValue: 'filled',
                selectSettings: {
                  options: Object.entries(MdIconButtonVariant)
                    .filter(
                      ([k, v]) =>
                        v !== MdIconButtonVariant.Elevated &&
                        v !== MdIconButtonVariant.FilledTonal &&
                        v !== MdIconButtonVariant.Outlined
                    )
                    .map(([label, value]) => ({
                      label,
                      value,
                    })),
                },
              },
              {
                id: ComponentSettingKey.CardDesignMediaIconBackgroundColorVariant,
                type: BuilderComponentSettingType.ColorVariant,
                label: LABELS.ICON_BACKGROUND_COLOR_KEY,
                defaultValue: 'primary-80',
                colorVariantSettings: {
                  fullColor: 'bg',
                  schemeId: ComponentSettingKey.CardDesignScheme,
                },
                dependOn: {
                  id: ComponentSettingKey.CardDesignMediaIconVariant,
                  showWhen: (v) => v === 'filled' || v === 'outlined',
                },
              },
              {
                id: ComponentSettingKey.CardDesignMediaIconColorVariant,
                type: BuilderComponentSettingType.ColorVariant,
                label: LABELS.MEDIA_ICON_KEY,
                defaultValue: 'primary-90',
                colorVariantSettings: {
                  fullColor: 'bg',
                  schemeId: ComponentSettingKey.CardDesignScheme,
                },
              },
              {
                id: ComponentSettingKey.CardDesignMediaIconColorCustom,
                type: BuilderComponentSettingType.Switch,
                label: 'Dynamic Color',
                defaultValue: false,
              },
              // {
              //   id: ComponentSettingKey.CardDesignMediaIconColorFilled,
              //   type: BuilderComponentSettingType.Switch,
              //   label: 'Filled',
              //   defaultValue: true,
              // },
              // {
              //   id: ComponentSettingKey.CardDesignMediaIconColorCustom,
              //   type: BuilderComponentSettingType.Switch,
              //   label: 'Dynamic Color',
              //   defaultValue: false,
              //   onValueChange: (v, settings, dbClient: IDB) => {
              //     const tableId = settings[ComponentSettingKey.CardDataSource]
              //     const table = dbClient.GetTable(tableId)

              //     const colorColumn = table?.settings._Columns.find((c) => c._Type === ColumnTypes.Color)

              //     if (v === true) {
              //       settings[ComponentSettingKey.CardDesignMediaIconColorColumn] = colorColumn?._id
              //     }
              //     return settings
              //   },
              // },
              {
                id: ComponentSettingKey.CardDesignMediaIconBackgroundColorColumn,
                type: BuilderComponentSettingType.ColumnSelect,
                label: 'Select Background Color Column',
                help: 'If no color found will fallback to the color variant settings.',
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.CardDataSource,
                  columnTypes: [ColumnTypes.Color],
                },
                dependOn: {
                  id: ComponentSettingKey.CardDesignMediaIconColorCustom,
                  showWhen: (v) => v === true,
                },
              },
              {
                id: ComponentSettingKey.CardDesignMediaIconColorColumn,
                type: BuilderComponentSettingType.ColumnSelect,
                label: 'Select Color Column',
                help: 'If no color found will fallback to the color variant settings.',
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.CardDataSource,
                  columnTypes: [ColumnTypes.Color],
                },
                dependOn: {
                  id: ComponentSettingKey.CardDesignMediaIconColorCustom,
                  showWhen: (v) => v === true,
                },
              },
              // {
              //   id: ComponentSettingKey.CardDesignMediaIconWeight,
              //   type: BuilderComponentSettingType.Slider,
              //   defaultValue: [200],
              //   label: 'Weight',
              //   sliderSettings: {
              //     max: 700,
              //     min: 100,
              //     range: false,
              //     step: 100,
              //     showMark: true,
              //   },
              // },
              // {
              //   id: ComponentSettingKey.CardDesignMediaIconGrade,
              //   type: BuilderComponentSettingType.SliderSelect,
              //   defaultValue: 0,
              //   label: 'Grade',
              //   sliderSelectSettings: {
              //     options: [-25, 0, 200],
              //   },
              // },
            ],
          },
          [LABELS.ORIENTATION_KEY]: {
            items: [
              {
                id: ComponentSettingKey.CardDesignOrientation,
                label: 'Scroll Direction',
                type: BuilderComponentSettingType.IconSelect,
                defaultValue: 'vertical',
                help: 'Horizontal orientation will make the list scrollable horizontally.',
                iconSelectSettings: {
                  options: [
                    {
                      id: 'horizontal',
                      icon: 'Horizontal24',
                      title: 'Horizontal',
                    },
                    {
                      id: 'vertical',
                      icon: 'Vertical24',
                      title: 'Vertical',
                    },
                  ],
                },
              },
              {
                id: ComponentSettingKey.CardDesignFooterButtonOrientation,
                label: 'Footer Button',
                type: BuilderComponentSettingType.IconSelect,
                defaultValue: 'horizontal',
                help: 'Horizontal orientation will make the footer button full width.',
                iconSelectSettings: {
                  options: [
                    {
                      id: 'horizontal',
                      icon: 'Horizontal24',
                      title: 'Horizontal',
                    },
                    {
                      id: 'vertical',
                      icon: 'Vertical24',
                      title: 'Vertical',
                    },
                  ],
                },
              },
            ],
          },
          [LABELS.ITEMS_KEY]: {
            items: [
              // makeNumItems(ComponentSettingKey.CardDataNumItems),
              {
                id: ComponentSettingKey.CardDataNumItems,
                label: 'Dataset Limit',
                type: BuilderComponentSettingType.DataSetLimit,
                defaultValue: {
                  enabled: false,
                  limit: 100,
                  value: 10,
                },
              },
              {
                id: ComponentSettingKey.CardDesignNumCols,
                label: LABELS.COLUMNS_KEY,
                type: BuilderComponentSettingType.Number,
                defaultValue: 1,
                numberSettings: {
                  max: 6,
                  min: 1,
                  step: 1,
                },
              },
              {
                id: ComponentSettingKey.CardDesignNumRows,
                label: LABELS.ROWS_KEY,
                type: BuilderComponentSettingType.Number,
                defaultValue: 100,
                numberSettings: {
                  max: 100,
                  min: 1,
                  step: 1,
                },
              },
              {
                id: ComponentSettingKey.CardDesignPagination,
                label: LABELS.SHOW_PAGINATION_KEY,
                type: BuilderComponentSettingType.Switch,
                defaultValue: true,
                // dependOn: {
                //   id: ComponentSettingKey.ListDesignOrientation,
                //   showWhen: (v) => v === 'vertical',
                // },
              },
              // {
              //   id: ComponentSettingKey.CardDesignColumnRow,
              //   label: 'Display',
              //   type: BuilderComponentSettingType.IconSelect,
              //   iconSelectSettings: {
              //     options: [
              //       {
              //         icon: 'Column16',
              //         id: 'column',
              //         title: 'Column',
              //       },
              //       {
              //         icon: 'Row16',
              //         id: 'row',
              //         title: 'Row',
              //       },
              //     ],
              //   },
              //   dependOn: {
              //     id: ComponentSettingKey.CardDesignOrientation,
              //     showWhen: (v) => v === 'horizontal',
              //   },
              // },
              {
                id: ComponentSettingKey.CardDesignSameHeight,
                label: 'Force Equal Height',
                type: BuilderComponentSettingType.Switch,
                help: 'Only works when card layout display is column',
                defaultValue: true,
              },
            ],
          },
          [LABELS.SPACING_KEY]: {
            items: [
              {
                id: ComponentSettingKey.CardDesignSpacingGroup,
                type: BuilderComponentSettingType.Group,
                label: '',
                groupItems: {
                  'Outer Structure': [
                    {
                      id: ComponentSettingKey.CardDesignMargin,
                      type: BuilderComponentSettingType.Spacing,
                      label: LABELS.COMPONENT_MARGIN_KEY,
                    },
                    {
                      id: ComponentSettingKey.CardDesignGroupHeaderMargin,
                      type: BuilderComponentSettingType.Spacing,
                      label: LABELS.GROUP_HEADER_MARGIN_KEY,
                    },
                    {
                      id: ComponentSettingKey.CardDesignFooterMargin,
                      type: BuilderComponentSettingType.Spacing,
                      label: LABELS.FOOTER_MARGIN_KEY,
                    },
                  ],
                  'Interactive Elements': [
                    {
                      id: ComponentSettingKey.CardDesignSearchMargin,
                      type: BuilderComponentSettingType.Spacing,
                      label: LABELS.SEARCH_BOX_KEY,
                    },
                    {
                      id: ComponentSettingKey.CardDesignFilterMargin,
                      type: BuilderComponentSettingType.Spacing,
                      label: LABELS.FILTERS_KEY,
                    },
                    {
                      id: ComponentSettingKey.CardDesignLeadingButtonPadding,
                      type: BuilderComponentSettingType.Spacing,
                      label: 'Padding Leading Button',
                    },
                    {
                      id: ComponentSettingKey.CardDesignTrailingButtonPadding,
                      type: BuilderComponentSettingType.Spacing,
                      label: 'Padding Trailing Button',
                    },
                  ],
                  Container: [
                    {
                      id: ComponentSettingKey.CardDesignPadding,
                      type: BuilderComponentSettingType.Spacing,
                      label: 'Component Padding',
                    },
                    {
                      type: BuilderComponentSettingType.SpacingSingle,
                      label: 'Item Gap',
                      id: ComponentSettingKey.CardDesignItemGap,
                      defaultValue: {
                        value: 10,
                        unit: SpacingInputUnit.Pixel,
                      },
                    },
                    {
                      id: ComponentSettingKey.CardDesignFooterGap,
                      type: BuilderComponentSettingType.SpacingSingle,
                      label: 'Footer Spacing',
                      defaultValue: {
                        value: 10,
                        unit: SpacingInputUnit.Pixel,
                      },
                    },
                  ],
                  Sections: [
                    {
                      id: ComponentSettingKey.CardDesignMediaMargin,
                      type: BuilderComponentSettingType.Spacing,
                      label: LABELS.MEDIA_MARGIN_KEY,
                    },
                    {
                      id: ComponentSettingKey.CardDesignHeadlineMargin,
                      type: BuilderComponentSettingType.Spacing,
                      label: LABELS.COMPONENT_TITLE_MARGIN_KEY,
                    },
                    {
                      id: ComponentSettingKey.CardDesignMetaMargin,
                      type: BuilderComponentSettingType.Spacing,
                      label: 'Metadata Margin',
                    },
                  ],
                  Spacing: [
                    {
                      type: BuilderComponentSettingType.SpacingSingle,
                      label: 'Header Spacing',
                      id: ComponentSettingKey.CardDesignHeaderSpacing,
                      defaultValue: {
                        value: 8,
                        unit: SpacingInputUnit.Pixel,
                      },
                    },
                    {
                      type: BuilderComponentSettingType.SpacingSingle,
                      label: 'Content Title Spacing',
                      id: ComponentSettingKey.CardDesignContentTitleSpacing,
                      defaultValue: {
                        value: 8,
                        unit: SpacingInputUnit.Pixel,
                      },
                    },
                    {
                      type: BuilderComponentSettingType.SpacingSingle,
                      label: 'Content Subtitle Spacing',
                      id: ComponentSettingKey.CardDesignContentSubtitleSpacing,
                      defaultValue: {
                        value: 8,
                        unit: SpacingInputUnit.Pixel,
                      },
                    },
                    {
                      type: BuilderComponentSettingType.SpacingSingle,
                      label: 'Supporting Text Spacing',
                      id: ComponentSettingKey.CardDesignSupportingTextSpacing,
                      defaultValue: {
                        value: 8,
                        unit: SpacingInputUnit.Pixel,
                      },
                    },
                    {
                      type: BuilderComponentSettingType.SpacingSingle,
                      label: 'Metadata Spacing',
                      id: ComponentSettingKey.CardDesignMetadataSpacing,
                      defaultValue: {
                        value: 8,
                        unit: SpacingInputUnit.Pixel,
                      },
                    },
                    {
                      type: BuilderComponentSettingType.SpacingSingle,
                      label: 'Image Spacing',
                      id: ComponentSettingKey.CardDesignImageSpacing,
                      defaultValue: {
                        value: 8,
                        unit: SpacingInputUnit.Pixel,
                      },
                    },
                    {
                      type: BuilderComponentSettingType.SpacingSingle,
                      label: 'Footer Section Spacing',
                      id: ComponentSettingKey.CardDesignFooterSectionSpacing,
                      defaultValue: {
                        value: 8,
                        unit: SpacingInputUnit.Pixel,
                      },
                    },
                    {
                      type: BuilderComponentSettingType.SpacingSingle,
                      label: 'Footer Title Spacing',
                      id: ComponentSettingKey.CardDesignFooterTitleSpacing,
                      defaultValue: {
                        value: 8,
                        unit: SpacingInputUnit.Pixel,
                      },
                    },
                  ],
                },
              },

              // {
              //   id: ComponentSettingKey.CardDesignItemPadding,
              //   type: BuilderComponentSettingType.Spacing,
              //   label: 'Item Padding',
              // },
            ],
          },
          [LABELS.ASPECT_RATIO_KEY]: {
            items: [
              {
                id: ComponentSettingKey.CardDesignMediaAspectRatio,
                type: BuilderComponentSettingType.Select,
                label: 'Media (Image)',
                defaultValue: AspectRatio.FourByThree,
                selectSettings: {
                  options: Object.values(AspectRatio).map((v) => {
                    return {
                      value: v,
                      label: getRatioLabel(v),
                      icon: 'AspectAuto16',
                    }
                  }),
                },
              },
            ],
          },
          // [LABELS.DIVIDER_KEY]: {
          //   items: [
          //     {
          //       id: ComponentSettingKey.CardDesignDividerBetweenTitle,
          //       type: BuilderComponentSettingType.Switch,
          //       label: 'Between Title and Supporting Text',
          //       help: 'Only if title and supporting text are presents.',
          //     },
          //     //!* Hid per 06 March
          //     /*{
          //       id: ComponentSettingKey.CardDesignDivider,
          //       type: BuilderComponentSettingType.Switch,
          //       label: 'Between Items',
          //     },*/
          //   ],
          // },
          [LABELS.ALIGNMENT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.CardDesignCompHAlign,
                type: BuilderComponentSettingType.ComponentAlignment,
                defaultValue: 'parent',
                label: LABELS.COMPONENT_ALIGNMENT_KEY,
              },
              {
                id: ComponentSettingKey.CardDesignCompTitleAlign,
                type: BuilderComponentSettingType.HorizontalAlignment,
                defaultValue: 'start',
                label: LABELS.COMPONENT_TITLE_KEY,
              },
              {
                id: ComponentSettingKey.CardDesignGroupHeaderAlign,
                type: BuilderComponentSettingType.HorizontalAlignment,
                defaultValue: 'start',
                label: LABELS.GROUP_HEADER_KEY,
              },
              {
                id: ComponentSettingKey.CardDesignHeaderTitleAlignment,
                label: LABELS.LINE1_KEY,
                type: BuilderComponentSettingType.HorizontalAlignment,
                defaultValue: 'start',
              },
              {
                id: ComponentSettingKey.CardDesignHeaderSubtitleAlignment,
                label: LABELS.LINE2_KEY,
                type: BuilderComponentSettingType.HorizontalAlignment,
                defaultValue: 'start',
              },
              {
                id: ComponentSettingKey.CardDesignTitleAlignment,
                label: LABELS.LINE3_KEY,
                type: BuilderComponentSettingType.HorizontalAlignment,
                defaultValue: 'start',
              },
              {
                id: ComponentSettingKey.CardDesignSubtitleAlignment,
                label: LABELS.LINE4_KEY,
                type: BuilderComponentSettingType.HorizontalAlignment,
                defaultValue: 'start',
              },
              {
                id: ComponentSettingKey.CardDesignSupportingTextAlignment,
                label: LABELS.LINE5_KEY,
                type: BuilderComponentSettingType.HorizontalAlignment,
                defaultValue: 'start',
              },
              {
                id: ComponentSettingKey.CardDesignMetaAlignment,
                label: LABELS.METADATA_KEY,
                type: BuilderComponentSettingType.HorizontalAlignment,
                defaultValue: 'start',
              },
              {
                id: ComponentSettingKey.CardDesignActionAlignment,
                label: LABELS.ACTION_BUTTON_KEY,
                type: BuilderComponentSettingType.HorizontalAlignment,
                defaultValue: 'start',
              },
              {
                id: ComponentSettingKey.CardDesignMediaAlignment,
                label: LABELS.MEDIA_CARD_KEY,
                type: BuilderComponentSettingType.HorizontalAlignment,
                defaultValue: 'start',
              },
              {
                id: ComponentSettingKey.CardDesignSearchAlignment,
                label: LABELS.SEARCH_BOX_KEY,
                type: BuilderComponentSettingType.HorizontalAlignment,
                defaultValue: 'start',
              },
              {
                id: ComponentSettingKey.CardDesignPaginationAlignment,
                label: LABELS.PAGINATION_KEY,
                type: BuilderComponentSettingType.HorizontalAlignment,
                defaultValue: 'start',
              },
            ],
          },
          [LABELS.TYPOGRAPHY_KEY]: makeTypographyGroup(ComponentSettingKey.CardDesignTypography, [
            {
              id: ComponentSettingKey.CardDesignHeaderTypography,
              label: LABELS.LINE1_KEY,
              defaultValue: 'Title Medium',
            },
            {
              id: ComponentSettingKey.CardDesignSubHeaderTypography,
              label: LABELS.LINE2_KEY,
              defaultValue: 'Body Medium',
            },
            {
              id: ComponentSettingKey.CardDesignTitleTypography,
              label: LABELS.LINE3_KEY,
              defaultValue: 'Body Large',
            },
            {
              id: ComponentSettingKey.CardDesignSubtitleTypography,
              label: LABELS.LINE4_KEY,
              defaultValue: 'Body Medium',
            },
            {
              id: ComponentSettingKey.CardDesignSupportingTextTypography,
              label: LABELS.LINE5_KEY,
              defaultValue: 'Body Medium',
            },
            {
              id: ComponentSettingKey.CardDesignMetadataTypography,
              label: LABELS.METADATA_KEY,
              defaultValue: 'Body Small',
            },
            {
              id: ComponentSettingKey.CardDesignActionHelpTypography,
              label: LABELS.FOOTER_TITLE_KEY,
              defaultValue: 'Body Small',
            },
            {
              id: ComponentSettingKey.CardDesignHeadlineTypography,
              label: LABELS.COMPONENT_TITLE_KEY,
              defaultValue: 'headline-small',
            },
            {
              id: ComponentSettingKey.CardDesignGroupHeaderTypography,
              label: LABELS.GROUP_HEADER_KEY,
              defaultValue: 'Title Medium',
            },
            {
              id: ComponentSettingKey.CardDesignLeadingButtonTypography,
              label: 'Leading Button',
              defaultValue: 'Label Medium',
            },
            {
              id: ComponentSettingKey.CardDesignTrailingButtonTypography,
              label: 'Trailing Button',
              defaultValue: 'Label Medium',
            },
          ]),
          [LABELS.TEXT_LINES_KEY]: {
            items: [
              {
                type: BuilderComponentSettingType.Slider,
                label: LABELS.LINE1_KEY,
                id: ComponentSettingKey.CardDesignHeaderLines,
                defaultValue: [4],
                sliderSettings: {
                  max: 10,
                  min: 1,
                  step: 1,
                  range: false,
                  unit: '',
                  showUnit: false,
                },
              },
              {
                type: BuilderComponentSettingType.Slider,
                label: LABELS.LINE2_KEY,
                id: ComponentSettingKey.CardDesignSubHeaderLines,
                defaultValue: [4],
                sliderSettings: {
                  max: 10,
                  min: 1,
                  step: 1,
                  range: false,
                  unit: '',
                  showUnit: false,
                },
              },
              {
                type: BuilderComponentSettingType.Slider,
                label: LABELS.LINE3_KEY,
                id: ComponentSettingKey.CardDesignTitleLines,
                defaultValue: [4],
                sliderSettings: {
                  max: 10,
                  min: 1,
                  step: 1,
                  range: false,
                  unit: '',
                  showUnit: false,
                },
              },
              {
                type: BuilderComponentSettingType.Slider,
                label: LABELS.LINE4_KEY,
                id: ComponentSettingKey.CardDesignSubtitleLines,
                defaultValue: [4],
                sliderSettings: {
                  max: 10,
                  min: 1,
                  step: 1,
                  range: false,
                  unit: '',
                  showUnit: false,
                },
              },
              {
                type: BuilderComponentSettingType.Slider,
                label: LABELS.LINE5_KEY,
                id: ComponentSettingKey.CardDesignSupportingTextLines,
                defaultValue: [4],
                sliderSettings: {
                  max: 10,
                  min: 1,
                  step: 1,
                  range: false,
                  unit: '',
                  showUnit: false,
                },
              },
              {
                type: BuilderComponentSettingType.Slider,
                label: LABELS.METADATA_KEY,
                id: ComponentSettingKey.CardDesignMetadataLines,
                defaultValue: [4],
                sliderSettings: {
                  max: 10,
                  min: 1,
                  step: 1,
                  range: false,
                  unit: '',
                  showUnit: false,
                },
              },
            ],
          },
          [LABELS.HOVER_EFFECT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.CardDesignHoverEffect,
                type: BuilderComponentSettingType.Select,
                label: LABELS.HOVER_EFFECT_KEY,
                selectSettings: {
                  options: hoverEffectOptions,
                },
              },
            ],
          },
        },
        action: [
          {
            id: ComponentSettingKey.CardActionItemClick,
            label: LABELS.ON_ITEM_CLICK_KEY,
            isSimple: false,
            defaultValue: [
              {
                type: IActionType.ShowDetailsScreen,
                settings: {
                  screenType: 'overlay',
                  overlaySettings: {
                    closeBehavior: 'direct',
                    placement: {
                      phone: 'bottom',
                      'phone-landscape': 'left',
                      tablet: 'bottom',
                      'tablet-landscape': 'left',
                      desktop: 'left',
                      tv: 'left',
                    },
                    size: {
                      phone: '90%, 100%',
                      'phone-landscape': '50%, 100%',
                      tablet: '90%, 100%',
                      'tablet-landscape': '50%, 100%',
                      desktop: '40%, 100%',
                      tv: '40%, 100%',
                    },
                    shape: {
                      phone: 'medium',
                      'phone-landscape': 'medium',
                      tablet: 'medium',
                      'tablet-landscape': 'medium',
                      desktop: 'medium',
                      tv: 'medium',
                    },
                    opacity: 85,
                  },
                },
              },
            ],
          },
          {
            id: ComponentSettingKey.CardActionLeadingClick,
            label: 'On Leading Action Button Click',
            isSimple: false,
          },
          {
            id: ComponentSettingKey.CardActionTrailingClick,
            label: 'On Trailing Action Button Click',
            isSimple: false,
          },
          {
            id: ComponentSettingKey.CardActionAddButtonClick,
            label: 'On Add Button Click',
            isSimple: false,
            showLabel: true,
            defaultValue: [
              {
                type: IActionType.FormAddItemScreen,
                settings: {
                  screenType: 'overlay',
                },
              },
            ],
            dataSource: ComponentSettingKey.CardDataSource,
            actionTypes: [IActionType.FormAddItemScreen],
          },
        ],
        behavior: {
          custom: {
            [LABELS.APP_FILTERS_KEY]: {
              items: [
                {
                  id: ComponentSettingKey.CardBehaviorUserFilter,
                  label: '',
                  type: BuilderComponentSettingType.UserColumnFilter,
                  columnFilterSettings: {
                    tableSourceId: ComponentSettingKey.CardDataSource,
                  },
                },
              ],
            },
            [LABELS.USER_SEARCH_KEY]: {
              items: [
                {
                  id: ComponentSettingKey.CardBehaviorUserSearch,
                  type: BuilderComponentSettingType.Switch,
                  defaultValue: true,
                  label: LABELS.ENABLE_USER_SEARCH,
                },
                {
                  id: ComponentSettingKey.CardBehaviorSearchResultsInfo,
                  type: BuilderComponentSettingType.Switch,
                  defaultValue: true,
                  label: LABELS.SHOW_SEARCH_RESULTS_INFO_KEY,
                },
              ],
            },
            [LABELS.ADD_BUTTON_KEY]: {
              items: [
                {
                  id: ComponentSettingKey.CardBehaviorAddButtonShow,
                  type: BuilderComponentSettingType.Switch,
                  label: 'Show',
                  defaultValue: true,
                },
                {
                  id: ComponentSettingKey.CardBehaviorAddButtonPosition,
                  type: BuilderComponentSettingType.Select,
                  label: 'Button Position',
                  defaultValue: 'inline',
                  forceHorizontal: true,
                  selectSettings: {
                    options: [
                      {
                        value: 'inline',
                        label: 'Inline',
                      },
                      {
                        value: 'bottom',
                        label: 'Bottom',
                      },
                    ],
                  },
                },
                {
                  id: ComponentSettingKey.CardBehaviorAddButtonVisibility,
                  type: BuilderComponentSettingType.Visibility,
                  label: 'Show Add Button',
                  defaultValue: {
                    enabled: true,
                    conditions: [
                      {
                        lhs: {
                          col: 'PROFILE_ROLE',
                          flag: ConditionFeatureFlag.ProfileData,
                        },
                        operator: 'text-is-equal-to',
                        rhs: {
                          flag: 4,
                          value: 'admin',
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          visibility: true,
        },
      },
    },

    // gallery
    {
      compId: 'gallery',
      title: LABELS.GALLERY_KEY,
      searchKeywords: ['gallery', 'photo grid', 'image collection', 'album', 'photo wall', 'gallery view'],
      icon: 'Gallery16',
      nested: false,
      //detailDataSource: undefined,
      settings: {
        dataGroup: {
          [LABELS.DATA_SOURCE_KEY]: {
            items: [
              makeDataSource(ComponentSettingKey.GalleryDataSource),
              {
                id: ComponentSettingKey.GalleryDataImage,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.IMAGE_KEY,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.GalleryDataSource,
                  columnTypes: [
                    ColumnTypes.Image,
                    ColumnTypes.URL,
                    ColumnTypes.Attachment,
                    ColumnTypes.Lookup,
                    ColumnTypes.Barcode,
                    ColumnTypes.QRCode,
                  ],
                  includeLookupType: true,
                },
              },
              {
                id: ComponentSettingKey.GalleryDataCaption,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.CAPTION_KEY,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.GalleryDataSource,
                  columnTypes: withoutSlugColumnTypes,
                },
              },
              {
                id: ComponentSettingKey.GalleryDataAvatar,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.AVATAR_KEY,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.GalleryDataSource,
                  columnTypes: [
                    ColumnTypes.Image,
                    ColumnTypes.URL,
                    ColumnTypes.Attachment,
                    ColumnTypes.Lookup,
                    ColumnTypes.Barcode,
                    ColumnTypes.QRCode,
                  ],
                  includeLookupType: true,
                },
              },
              {
                id: ComponentSettingKey.GalleryDataLike,
                type: BuilderComponentSettingType.ColumnSelect,
                label: 'Reaction',
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.GalleryDataSource,
                  columnTypes: [ColumnTypes.Boolean],
                },
              },
              {
                id: ComponentSettingKey.GalleryDataTag,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.TAG_KEY,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.GalleryDataSource,
                  columnTypes: withoutSlugColumnTypes,
                },
              },
            ],
          },
          [LABELS.COMPONENT_TITLE_KEY]: {
            items: [
              {
                id: ComponentSettingKey.GalleryDataCompTitle,
                type: BuilderComponentSettingType.VariableText,
                label: '',
                defaultValue: [
                  {
                    type: 'paragraph',
                    children: [{ text: '' }],
                  },
                ],
                variableTextSettings: {
                  flags:
                    VARIABLE_TEXT_FEATURE_FLAG_ALL &
                    ~VariableTextFeatureFlag.ColumnData &
                    ~VariableTextFeatureFlag.Image,
                },
              },
            ],
          },
          [LABELS.SORTING_KEY]: makeSorting(
            ComponentSettingKey.GalleryDataSortBy,
            ComponentSettingKey.GalleryDataSortType,
            ComponentSettingKey.GalleryDataSource
          ),
          [LABELS.DATA_FILTER_KEY]: makeFilter(
            ComponentSettingKey.GalleryDataFilter,
            ComponentSettingKey.GalleryDataSource
          ),
          [LABELS.GROUPING_KEY]: {
            items: [
              {
                id: ComponentSettingKey.GalleryDataGroupBy,
                label: LABELS.GROUP_BY_KEY,
                type: BuilderComponentSettingType.ColumnSelect,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.GalleryDataSource,
                  noAutoSelect: true,
                  includeLookupType: true,
                },
              },
            ],
          },
        },
        designGroup: {
          [LABELS.COLORS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.GalleryDesignSearchBgColor,
                type: BuilderComponentSettingType.ColorVariant,
                label: 'Search Box Background',
                defaultValue: 'primary-90',
              },
              {
                id: ComponentSettingKey.GalleryDesignFilterChipColor,
                type: BuilderComponentSettingType.ColorVariant,
                label: LABELS.FILTERS_CHIP_KEY,
                defaultValue: 'primary-95',
              },
              {
                id: ComponentSettingKey.GalleryDesignFilterBgColor,
                type: BuilderComponentSettingType.ColorVariant,
                label: LABELS.FILTERS_BG_KEY,
                defaultValue: 'primary-95',
              },
              {
                id: ComponentSettingKey.GalleryDesignAddButtonColor,
                type: BuilderComponentSettingType.ColorVariant,
                label: 'Add Button Color',
                defaultValue: 'primary-20',
              },
            ],
          },
          [LABELS.DIMENSION_KEY]: {
            items: [
              {
                id: ComponentSettingKey.GalleryDesignWidth,
                type: BuilderComponentSettingType.Slider,
                label: LABELS.GALLERY_KEY,
                defaultValue: [100],
                sliderSettings: {
                  max: 100,
                  min: 10,
                  step: 1,
                  range: false,
                  unit: '%',
                  showUnit: true,
                },
              },
              {
                id: ComponentSettingKey.GalleryDesignSearchWidth,
                type: BuilderComponentSettingType.Slider,
                label: LABELS.SEARCH_BOX_KEY,
                defaultValue: [100],
                sliderSettings: {
                  max: 100,
                  min: 10,
                  step: 1,
                  range: false,
                  unit: '%',
                  showUnit: true,
                },
              },
            ],
          },
          [LABELS.ALIGNMENT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.GalleryDesignCompHAlign,
                type: BuilderComponentSettingType.ComponentAlignment,
                defaultValue: 'parent',
                label: LABELS.COMPONENT_ALIGNMENT_KEY,
              },
              {
                id: ComponentSettingKey.GalleryDesignCompItemsAlign,
                type: BuilderComponentSettingType.HorizontalAlignment,
                defaultValue: 'start',
                label: 'Items Alignment',
              },
              {
                id: ComponentSettingKey.GalleryDesignGroupHeaderAlign,
                type: BuilderComponentSettingType.HorizontalAlignment,
                defaultValue: 'center',
                label: LABELS.GROUP_HEADER_ALIGNMENT,
              },
              {
                id: ComponentSettingKey.GalleryDesignCompTitleAlign,
                type: BuilderComponentSettingType.HorizontalAlignment,
                defaultValue: 'center',
                label: LABELS.COMPONENT_TITLE_ALIGNMENT_KEY,
              },
              {
                id: ComponentSettingKey.GalleryDesignSearchAlignment,
                type: BuilderComponentSettingType.HorizontalAlignment,
                defaultValue: 'center',
                label: LABELS.SEARCH_BOX_KEY,
              },
              {
                id: ComponentSettingKey.GalleryDesignPaginationAlign,
                label: 'Pagination',
                type: BuilderComponentSettingType.HorizontalAlignment,
                defaultValue: 'start',
              },
            ],
          },
          [LABELS.LAYOUT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.GalleryDesignLayout,
                label: LABELS.STYLE_KEY,
                defaultValue: 'grid',
                type: BuilderComponentSettingType.Select,
                selectSettings: {
                  options: [
                    {
                      label: 'Grid',
                      value: 'grid',
                    },
                    {
                      label: 'Masonry',
                      value: 'masonry',
                    },
                    // {
                    //   label: 'Carousel',
                    //   value: 'carousel',
                    // },
                    // {
                    //   label: 'List',
                    //   value: 'list',
                    // },
                  ],
                },
              },
            ],
          },
          [LABELS.ITEMS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.GalleryDataNumItems,
                label: 'Dataset Limit',
                type: BuilderComponentSettingType.DataSetLimit,
                defaultValue: {
                  enabled: false,
                  limit: 100,
                  value: 10,
                },
              },
              {
                id: ComponentSettingKey.GalleryDesignNumCols,
                label: LABELS.COLUMNS_KEY,
                type: BuilderComponentSettingType.Number,
                defaultValue: 1,
                numberSettings: {
                  max: 10,
                  min: 1,
                  step: 1,
                },
                dependOn: {
                  id: ComponentSettingKey.GalleryDesignLayout,
                  showWhen: (v) => v !== MdGalleryLayout.Carousel,
                },
              },
              {
                id: ComponentSettingKey.GalleryDesignNumRows,
                type: BuilderComponentSettingType.Number,
                label: 'Rows',
                defaultValue: 4,
                numberSettings: {
                  max: 10,
                  min: 1,
                  step: 1,
                },
                dependOn: {
                  id: ComponentSettingKey.GalleryDesignLayout,
                  showWhen: (v) => v === MdGalleryLayout.Grid,
                },
              },
              {
                id: ComponentSettingKey.GalleryDesignCarouselArrow,
                type: BuilderComponentSettingType.Switch,
                label: LABELS.CAROUSEL_ARROWS,
                defaultValue: true,
                dependOn: {
                  id: ComponentSettingKey.GalleryDesignLayout,
                  showWhen: (v) => v === MdGalleryLayout.Carousel,
                },
              },
              {
                id: ComponentSettingKey.GalleryDesignCarouselIndicator,
                type: BuilderComponentSettingType.Switch,
                label: LABELS.CAROUSEL_INDICATORS,
                defaultValue: false,
                dependOn: {
                  id: ComponentSettingKey.GalleryDesignLayout,
                  showWhen: (v) => v === MdGalleryLayout.Carousel,
                },
              },
              {
                id: ComponentSettingKey.GalleryDesignCarousellCaptionVAlign,
                type: BuilderComponentSettingType.BottomMidleAlignment,
                label: 'Caption Alignment',
                dependOn: {
                  id: ComponentSettingKey.GalleryDesignLayout,
                  showWhen: (v) => v === MdGalleryLayout.Carousel,
                },
              },

              {
                id: ComponentSettingKey.GalleryDesignPagination,
                type: BuilderComponentSettingType.Switch,
                label: 'Show Pagination',
                defaultValue: true,
                dependOn: {
                  id: ComponentSettingKey.GalleryDesignLayout,
                  showWhen: (v) => v !== MdGalleryLayout.Carousel,
                },
              },
            ],
          },
          [LABELS.CORNER_RADIUS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.GalleryDesignImageShape,
                type: BuilderComponentSettingType.Select,
                label: LABELS.IMAGE_KEY,
                defaultValue: MdShape.Medium,
                selectSettings: {
                  options: Object.entries(MdShape).map(([label, value]) => ({
                    value,
                    label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
                  })),
                },
              },
              {
                id: ComponentSettingKey.GalleryDesignAvatarShape,
                type: BuilderComponentSettingType.Select,
                label: LABELS.AVATAR_KEY,
                defaultValue: MdShape.Full,
                selectSettings: {
                  options: Object.entries(MdShape).map(([label, value]) => ({
                    value,
                    label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
                  })),
                },
              },
              {
                id: ComponentSettingKey.GalleryDesignSearchCorner,
                type: BuilderComponentSettingType.Select,
                label: LABELS.SEARCH_BOX_KEY,
                defaultValue: MdShape.Medium,
                selectSettings: {
                  options: Object.entries(MdShape).map(([label, value]) => ({
                    value,
                    label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
                  })),
                },
              },
              {
                id: ComponentSettingKey.GalleryDesignFilterCornerRadius,
                label: LABELS.FILTERS_CHIP_KEY,
                type: BuilderComponentSettingType.Select,
                defaultValue: MdShape.Medium,
                selectSettings: {
                  options: Object.entries(MdShape).map(([label, value]) => ({
                    value,
                    label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
                  })),
                },
              },
              {
                id: ComponentSettingKey.GalleryDesignAddButtonCornerRadius,
                label: 'Add Button',
                type: BuilderComponentSettingType.Select,
                defaultValue: MdShape.Medium,
                selectSettings: {
                  options: Object.entries(MdShape).map(([label, value]) => ({
                    value,
                    label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
                  })),
                },
              },
            ],
          },
          Like: {
            items: [
              {
                id: ComponentSettingKey.GalleryDesignLikeIcon,
                label: 'Like Icon',
                type: BuilderComponentSettingType.Icon,
                defaultValue: 'star',
              },
            ],
          },
          [LABELS.SPACING_KEY]: {
            items: [
              {
                id: ComponentSettingKey.GalleryDesignMargin,
                type: BuilderComponentSettingType.Spacing,
                label: LABELS.MARGIN_KEY,
              },
              {
                id: ComponentSettingKey.GalleryDesignHeadlineMargin,
                type: BuilderComponentSettingType.Spacing,
                label: LABELS.COMPONENT_TITLE_MARGIN_KEY,
              },
              {
                id: ComponentSettingKey.GalleryDesignGroupHeaderMargin,
                type: BuilderComponentSettingType.Spacing,
                label: LABELS.GROUP_HEADER_MARGIN_KEY,
              },
              {
                id: ComponentSettingKey.GalleryDesignGap,
                type: BuilderComponentSettingType.SpacingSingle,
                label: 'Item gap',
                defaultValue: {
                  value: 16,
                  unit: SpacingInputUnit.Pixel,
                },
              },
              {
                id: ComponentSettingKey.GalleryDesignSearchMargin,
                type: BuilderComponentSettingType.Spacing,
                label: LABELS.SEARCH_BOX_KEY,
              },
              {
                id: ComponentSettingKey.GalleryDesignFilterMargin,
                type: BuilderComponentSettingType.Spacing,
                label: LABELS.FILTERS_KEY,
              },
            ],
          },
          [LABELS.ASPECT_RATIO_KEY]: {
            items: [
              {
                id: ComponentSettingKey.GalleryDesignAspectRatio,
                type: BuilderComponentSettingType.Select,
                label: '',
                help: 'Will be ignored when layout variant is masonry',
                defaultValue: AspectRatio.OneByOne,
                selectSettings: {
                  options: Object.values(AspectRatio).map((v) => {
                    return {
                      value: v,
                      label: getRatioLabel(v),
                      icon: 'AspectAuto16',
                    }
                  }),
                },
              },
            ],
          },
          [LABELS.TYPOGRAPHY_KEY]: makeTypographyGroup(ComponentSettingKey.GalleryDesignTypography, [
            {
              id: ComponentSettingKey.GalleryDesignCaptionTypography,
              label: LABELS.CAPTION_KEY,
              defaultValue: 'Body Small',
            },
            {
              id: ComponentSettingKey.GalleryDesignTagTypography,
              label: LABELS.BADGE_KEY,
              defaultValue: 'Body Small',
            },
            {
              id: ComponentSettingKey.GalleryDesignGroupHeaderTypography,
              label: LABELS.GROUP_HEADER_KEY,
              defaultValue: 'Title Medium',
            },
            {
              id: ComponentSettingKey.GalleryDesignCompTitleTypography,
              label: LABELS.COMPONENT_TITLE_KEY,
              defaultValue: 'headline-small',
            },
          ]),
          [LABELS.HOVER_EFFECT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.GalleryDesignHoverEffect,
                type: BuilderComponentSettingType.Select,
                label: LABELS.HOVER_EFFECT_KEY,
                selectSettings: {
                  options: hoverEffectOptions,
                },
              },
            ],
          },
        },
        action: [
          {
            id: ComponentSettingKey.GalleryActionItemClick,
            label: LABELS.ON_ITEM_CLICK_KEY,
            isSimple: false,
            defaultValue: [
              {
                type: IActionType.ShowDetailsScreen,
                settings: {
                  overlaySettings: {
                    closeBehavior: 'direct',
                    opacity: 85,
                    placement: {
                      desktop: 'left',
                      phone: 'bottom',
                      'phone-landscape': 'left',
                      tablet: 'bottom',
                      'tablet-landscape': 'left',
                      tv: 'left',
                    },
                    shape: {
                      desktop: 'medium',
                      phone: 'medium',
                      'phone-landscape': 'medium',
                      tablet: 'medium',
                      'tablet-landscape': 'medium',
                      tv: 'medium',
                    },
                    size: {
                      desktop: '40%, 100%',
                      phone: '90%, 100%',
                      'phone-landscape': '50%, 100%',
                      tablet: '90%, 100%',
                      'tablet-landscape': '50%, 100%',
                      tv: '40%, 100%',
                    },
                  },
                  screenType: 'overlay',
                },
              },
            ],
            actionTypes: [IActionType.ShowDetailsScreen],
          },
          {
            id: ComponentSettingKey.GalleryActionAddButtonClick,
            label: 'On Add Button Click',
            isSimple: false,
            showLabel: true,
            defaultValue: [
              {
                type: IActionType.FormAddItemScreen,
                settings: {
                  screenType: 'overlay',
                },
              },
            ],
            dataSource: ComponentSettingKey.GalleryDataSource,
            actionTypes: [IActionType.FormAddItemScreen],
          },
        ],
        behavior: {
          custom: {
            [LABELS.APP_FILTERS_KEY]: {
              items: [
                {
                  id: ComponentSettingKey.GalleryBehaviorUserFilter,
                  label: '',
                  type: BuilderComponentSettingType.UserColumnFilter,
                  columnFilterSettings: {
                    tableSourceId: ComponentSettingKey.GalleryDataSource,
                  },
                },
              ],
            },
            [LABELS.USER_SEARCH_KEY]: {
              items: [
                {
                  id: ComponentSettingKey.GalleryBehaviorUserSearch,
                  type: BuilderComponentSettingType.Switch,
                  defaultValue: true,
                  label: LABELS.ENABLE_USER_SEARCH,
                },
                {
                  id: ComponentSettingKey.GalleryBehaviorSearchResultsInfo,
                  type: BuilderComponentSettingType.Switch,
                  defaultValue: true,
                  label: LABELS.SHOW_SEARCH_RESULTS_INFO_KEY,
                },
              ],
            },
            [LABELS.ADD_BUTTON_KEY]: {
              items: [
                {
                  id: ComponentSettingKey.GalleryBehaviorAddButtonShow,
                  type: BuilderComponentSettingType.Switch,
                  label: 'Show',
                  defaultValue: true,
                },
                {
                  id: ComponentSettingKey.GalleryBehaviorAddButtonPosition,
                  type: BuilderComponentSettingType.Select,
                  label: 'Button Position',
                  defaultValue: 'inline',
                  forceHorizontal: true,
                  selectSettings: {
                    options: [
                      {
                        value: 'inline',
                        label: 'Inline',
                      },
                      {
                        value: 'bottom',
                        label: 'Bottom',
                      },
                    ],
                  },
                },
                {
                  id: ComponentSettingKey.GalleryBehaviorAddButtonVisibility,
                  type: BuilderComponentSettingType.Visibility,
                  label: 'Show Add Button',
                  defaultValue: {
                    enabled: true,
                    conditions: [
                      {
                        lhs: {
                          col: 'PROFILE_ROLE',
                          flag: ConditionFeatureFlag.ProfileData,
                        },
                        operator: 'text-is-equal-to',
                        rhs: {
                          flag: 4,
                          value: 'admin',
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          visibility: true,
        },
      },
    },

    // accordion
    {
      compId: 'accordion',
      title: 'Accordion',
      searchKeywords: ['accordion', 'expand', 'collapse', 'dropdown', 'FAQ', 'disclosure', 'toggle panel', 'details'],
      icon: 'Accordion24',
      nested: false,
      //detailDataSource: undefined,
      settings: {
        dataGroup: {
          General: {
            items: [
              makeDataSource(ComponentSettingKey.AccordionDataSource),
              {
                id: ComponentSettingKey.AccordionDataTitle,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.TITLE_KEY,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.AccordionDataSource,
                  columnTypes: [
                    ColumnTypes.Text,
                    ColumnTypes.RichText,
                    ColumnTypes.Function,
                    ColumnTypes.URL,
                    ColumnTypes.Email,
                    ColumnTypes.Address,
                    ColumnTypes.Boolean,
                    ColumnTypes.Coordinate,
                    ColumnTypes.DateTime,
                    ColumnTypes.Join,
                    ColumnTypes.Color,
                    ColumnTypes.Number,
                    ColumnTypes.Phone,
                    ColumnTypes.RowID,
                    ColumnTypes.AISEO,
                    ColumnTypes.AITextProcessing,
                  ],
                },
              },
              {
                id: ComponentSettingKey.AccordionDataContent,
                type: BuilderComponentSettingType.ColumnSelect,
                label: 'Content',
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.AccordionDataSource,
                  columnTypes: withoutSlugColumnTypes,
                },
              },
              {
                id: ComponentSettingKey.AccordionDataIcon,
                label: 'Icon',
                type: BuilderComponentSettingType.Icon,
                defaultValue: 'expand_more',
              },
              {
                id: ComponentSettingKey.AccordionDataExpandedIcon,
                label: 'Expanded Icon',
                type: BuilderComponentSettingType.Icon,
                defaultValue: 'expand_less',
              },
            ],
          },
          [LABELS.COMPONENT_TITLE_KEY]: {
            items: [
              {
                id: ComponentSettingKey.AccordionDataCompTitle,
                type: BuilderComponentSettingType.VariableText,
                label: LABELS.COMPONENT_TITLE_KEY,
                defaultValue: [
                  {
                    type: 'paragraph',
                    children: [{ text: '' }],
                  },
                ],
                variableTextSettings: {
                  flags:
                    VARIABLE_TEXT_FEATURE_FLAG_ALL &
                    ~VariableTextFeatureFlag.ColumnData &
                    ~VariableTextFeatureFlag.Image,
                },
              },
            ],
          },
          [LABELS.SORTING_KEY]: makeSorting(
            ComponentSettingKey.AccordionDataSortBy,
            ComponentSettingKey.AccordionDataSortType,
            ComponentSettingKey.AccordionDataSource
          ),
          [LABELS.DATA_FILTER_KEY]: makeFilter(
            ComponentSettingKey.AccordionDataFilter,
            ComponentSettingKey.AccordionDataSource
          ),
          [LABELS.GROUPING_KEY]: {
            items: [
              {
                id: ComponentSettingKey.AccordionDataGroupBy,
                label: LABELS.GROUP_BY_KEY,
                help: 'Only works when in vertical layout mode',
                type: BuilderComponentSettingType.ColumnSelect,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.AccordionDataSource,
                  noAutoSelect: true,
                  includeLookupType: true,
                  columnTypes: [
                    ColumnTypes.Text,
                    ColumnTypes.RichText,
                    ColumnTypes.Function,
                    ColumnTypes.URL,
                    ColumnTypes.Email,
                    ColumnTypes.Address,
                    ColumnTypes.Boolean,
                    ColumnTypes.Coordinate,
                    ColumnTypes.DateTime,
                    ColumnTypes.Join,
                    ColumnTypes.Color,
                    ColumnTypes.Number,
                    ColumnTypes.Phone,
                    ColumnTypes.RowID,
                    ColumnTypes.AISEO,
                    ColumnTypes.AITextProcessing,
                  ],
                },
              },
            ],
          },
        },
        designGroup: {
          // [LABELS.COLOR_SCHEME_KEY]: {
          //   items: [
          //     // makeScheme(ComponentSettingKey.AccordionDesignScheme),
          //     // {
          //     //   id: ComponentSettingKey.AccordionDesignOutlineColor,
          //     //   label: LABELS.BORDER_KEY,
          //     //   type: BuilderComponentSettingType.ColorVariant,
          //     //   defaultValue: 'secondary-container',
          //     //   colorVariantSettings: {
          //     //     schemeId: ComponentSettingKey.AccordionDesignScheme,
          //     //     fullColor: 'both',
          //     //   },
          //     // },
          //     // {
          //     //   id: ComponentSettingKey.AccordionDesignTitleColor,
          //     //   label: LABELS.COMPONENT_TITLE_KEY,
          //     //   type: BuilderComponentSettingType.ColorVariant,
          //     //   defaultValue: 'on-primary',
          //     //   colorVariantSettings: {
          //     //     schemeId: ComponentSettingKey.AccordionDesignScheme,
          //     //     fullColor: 'fg',
          //     //   },
          //     // },
          //     // {
          //     //   id: ComponentSettingKey.AccordionDesignHeaderBgColor,
          //     //   label: 'Panel Header',
          //     //   type: BuilderComponentSettingType.ColorVariant,
          //     //   defaultValue: 'primary',
          //     //   colorVariantSettings: {
          //     //     schemeId: ComponentSettingKey.AccordionDesignScheme,
          //     //     fullColor: 'bg',
          //     //   },
          //     // },
          //     // {
          //     //   id: ComponentSettingKey.AccordionDesignHeaderItemColor,
          //     //   label: 'Panel Header Item',
          //     //   type: BuilderComponentSettingType.ColorVariant,
          //     //   defaultValue: 'on-primary',
          //     //   colorVariantSettings: {
          //     //     schemeId: ComponentSettingKey.AccordionDesignScheme,
          //     //     fullColor: 'fg',
          //     //   },
          //     // },
          //     // {
          //     //   id: ComponentSettingKey.AccordionDesignContentBgColor,
          //     //   label: 'Panel Content',
          //     //   type: BuilderComponentSettingType.ColorVariant,
          //     //   defaultValue: 'primary',
          //     //   colorVariantSettings: {
          //     //     schemeId: ComponentSettingKey.AccordionDesignScheme,
          //     //     fullColor: 'bg',
          //     //   },
          //     // },
          //     // {
          //     //   id: ComponentSettingKey.AccordionDesignContentItemColor,
          //     //   label: 'Panel Content Item',
          //     //   type: BuilderComponentSettingType.ColorVariant,
          //     //   defaultValue: 'on-primary',
          //     //   colorVariantSettings: {
          //     //     schemeId: ComponentSettingKey.AccordionDesignScheme,
          //     //     fullColor: 'fg',
          //     //   },
          //     // },
          //     // {
          //     //   id: ComponentSettingKey.AccordionDesignGroupHeaderColor,
          //     //   label: 'Panel Group Header',
          //     //   type: BuilderComponentSettingType.ColorVariant,
          //     //   defaultValue: 'on-primary',
          //     //   colorVariantSettings: {
          //     //     schemeId: ComponentSettingKey.AccordionDesignScheme,
          //     //     fullColor: 'fg',
          //     //   },
          //     // },
          //   ],
          // },
          // [LABELS.ICON_POSITION_KEY]: {
          //   items: [
          //     {
          //       id: ComponentSettingKey.AccordionDesignIconLeading,
          //       label: 'Leading Icon',
          //       type: BuilderComponentSettingType.Switch,
          //     },
          //   ],
          // },
          // [LABELS.BORDER_KEY]: {
          //   items: [
          //     {
          //       id: ComponentSettingKey.AccordionDesignOutlineBorderWidth,
          //       label: 'Border Width',
          //       type: BuilderComponentSettingType.Slider,
          //       defaultValue: [1],
          //       sliderSettings: {
          //         max: 20,
          //         min: 0,
          //         step: 0.25,
          //         unit: 'px',
          //       },
          //     },
          //   ],
          // },
          // [LABELS.CORNER_RADIUS_KEY]: {
          //   items: [
          //     {
          //       id: ComponentSettingKey.AccordionDesignRoundedCorner,
          //       type: BuilderComponentSettingType.Select,
          //       label: '',
          //       defaultValue: MdShape.Medium,
          //       selectSettings: {
          //         options: Object.entries(MdShape).map(([label, value]) => ({
          //           value,
          //           label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
          //         })),
          //       },
          //     },
          //   ],
          // },
          [LABELS.COLORS_KEY]: {
            items: [
              // [LABELS.SEARCH_BOX_KEY]: [
              //   {
              //     id: ComponentSettingKey.AccordionDesignSearchColor,
              //     type: BuilderComponentSettingType.ColorVariant,
              //     label: LABELS.SEARCH_BOX_KEY,
              //     defaultValue: 'primary-90',
              //     colorVariantSettings: {
              //       fullColor: 'bg',
              //       schemeId: ComponentSettingKey.AccordionDesignScheme,
              //     },
              //   },
              // ],
              {
                id: ComponentSettingKey.AccordionDesignSearchColor,
                type: BuilderComponentSettingType.ColorVariant,
                label: 'Search Box Background',
                defaultValue: 'primary-90',
              },
              {
                id: ComponentSettingKey.AccordionDesignAddButtonColor,
                type: BuilderComponentSettingType.ColorVariant,
                label: 'Add Button Color',
                defaultValue: 'primary-20',
              },
            ],
          },
          [LABELS.DIMENSION_KEY]: {
            items: [
              ...makeClampWidthItems(ComponentSettingKey.AccordionDesignWidth).items,
              {
                id: ComponentSettingKey.AccordionDesignSearchWidth,
                type: BuilderComponentSettingType.Slider,
                label: LABELS.SEARCH_BOX_KEY,
                defaultValue: [100],
                clampInAllDevices: true,
                sliderSettings: {
                  max: 3890,
                  min: 10,
                  step: 1,
                  range: false,
                  unit: '%',
                  showUnit: true,
                },
              },
            ],
          },
          [LABELS.SPACING_KEY]: {
            items: [
              {
                id: ComponentSettingKey.AccordionDesignTitleMargin,
                type: BuilderComponentSettingType.Spacing,
                label: 'Title Margin',
                defaultValue: {
                  top: {
                    value: 20,
                    unit: SpacingInputUnit.Pixel,
                  },
                  bottom: {
                    value: 20,
                    unit: SpacingInputUnit.Pixel,
                  },
                  left: {
                    value: 20,
                    unit: SpacingInputUnit.Pixel,
                  },
                  right: {
                    value: 20,
                    unit: SpacingInputUnit.Pixel,
                  },
                },
                // spacingSettings: {
                //   max: {
                //     [SpacingInputUnit.Em]: 4,
                //     [SpacingInputUnit.Rem]: 4,
                //     [SpacingInputUnit.Pixel]: Infinity,
                //     [SpacingInputUnit.Percent]: 10,
                //   },
                // },
              },
              {
                id: ComponentSettingKey.AccordionDesignComponentMargin,
                type: BuilderComponentSettingType.Spacing,
                label: 'Component Margin',
                defaultValue: {
                  top: {
                    value: 20,
                    unit: SpacingInputUnit.Pixel,
                  },
                  bottom: {
                    value: 20,
                    unit: SpacingInputUnit.Pixel,
                  },
                  left: {
                    value: 20,
                    unit: SpacingInputUnit.Pixel,
                  },
                  right: {
                    value: 20,
                    unit: SpacingInputUnit.Pixel,
                  },
                },
                // spacingSettings: {
                //   max: {
                //     [SpacingInputUnit.Em]: 4,
                //     [SpacingInputUnit.Rem]: 4,
                //     [SpacingInputUnit.Pixel]: Infinity,
                //     [SpacingInputUnit.Percent]: 10,
                //   },
                // },
              },
              // {
              //   id: ComponentSettingKey.AccordionDesignComponentPadding,
              //   type: BuilderComponentSettingType.Spacing,
              //   label: 'Component Padding',
              //   defaultValue: {
              //     top: {
              //       value: 20,
              //       unit: SpacingInputUnit.Pixel,
              //     },
              //     bottom: {
              //       value: 20,
              //       unit: SpacingInputUnit.Pixel,
              //     },
              //     left: {
              //       value: 20,
              //       unit: SpacingInputUnit.Pixel,
              //     },
              //     right: {
              //       value: 20,
              //       unit: SpacingInputUnit.Pixel,
              //     },
              //   },
              //   spacingSettings: {
              //     max: {
              //       [SpacingInputUnit.Em]: 4,
              //       [SpacingInputUnit.Rem]: 4,
              //       [SpacingInputUnit.Pixel]: Infinity,
              //       [SpacingInputUnit.Percent]: 10,
              //     },
              //   },
              // },
              // {
              //   id: ComponentSettingKey.AccordionDesignPanelHeaderMargin,
              //   type: BuilderComponentSettingType.Spacing,
              //   label: 'Header Padding',
              //   defaultValue: {
              //     top: {
              //       value: 30,
              //       unit: SpacingInputUnit.Pixel,
              //     },
              //     bottom: {
              //       value: 30,
              //       unit: SpacingInputUnit.Pixel,
              //     },
              //     left: {
              //       value: 30,
              //       unit: SpacingInputUnit.Pixel,
              //     },
              //     right: {
              //       value: 30,
              //       unit: SpacingInputUnit.Pixel,
              //     },
              //   },
              //   spacingSettings: {
              //     max: {
              //       [SpacingInputUnit.Em]: 4,
              //       [SpacingInputUnit.Rem]: 4,
              //       [SpacingInputUnit.Pixel]: Infinity,
              //       [SpacingInputUnit.Percent]: 10,
              //     },
              //   },
              // },
              // {
              //   id: ComponentSettingKey.AccordionDesignPanelContentMargin,
              //   type: BuilderComponentSettingType.Spacing,
              //   label: 'Content Padding',
              //   defaultValue: {
              //     top: {
              //       value: 30,
              //       unit: SpacingInputUnit.Pixel,
              //     },
              //     bottom: {
              //       value: 30,
              //       unit: SpacingInputUnit.Pixel,
              //     },
              //     left: {
              //       value: 30,
              //       unit: SpacingInputUnit.Pixel,
              //     },
              //     right: {
              //       value: 30,
              //       unit: SpacingInputUnit.Pixel,
              //     },
              //   },
              //   spacingSettings: {
              //     max: {
              //       [SpacingInputUnit.Em]: 4,
              //       [SpacingInputUnit.Rem]: 4,
              //       [SpacingInputUnit.Pixel]: Infinity,
              //       [SpacingInputUnit.Percent]: 10,
              //     },
              //   },
              // },
              {
                id: ComponentSettingKey.AccordionDesignGroupHeaderMargin,
                type: BuilderComponentSettingType.Spacing,
                label: 'Group Header Margin',
                defaultValue: {
                  top: {
                    value: 5,
                    unit: SpacingInputUnit.Pixel,
                  },
                  bottom: {
                    value: 5,
                    unit: SpacingInputUnit.Pixel,
                  },
                  left: {
                    value: 5,
                    unit: SpacingInputUnit.Pixel,
                  },
                  right: {
                    value: 5,
                    unit: SpacingInputUnit.Pixel,
                  },
                },
                // spacingSettings: {
                //   max: {
                //     [SpacingInputUnit.Em]: 4,
                //     [SpacingInputUnit.Rem]: 4,
                //     [SpacingInputUnit.Pixel]: Infinity,
                //     [SpacingInputUnit.Percent]: 10,
                //   },
                // },
              },
              {
                id: ComponentSettingKey.AccordionDesignSearchMargin,
                type: BuilderComponentSettingType.Spacing,
                label: LABELS.SEARCH_BOX_KEY,
              },
            ],
          },
          [LABELS.ALIGNMENT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.AccordionDesignAlignmentComponent,
                type: BuilderComponentSettingType.ComponentAlignment,
                label: LABELS.COMPONENT_ALIGNMENT_KEY,
              },
              {
                id: ComponentSettingKey.AccordionDesignAlignmentTitle,
                type: BuilderComponentSettingType.HorizontalAlignment,
                label: LABELS.COMPONENT_TITLE_KEY,
              },
              // {
              //   id: ComponentSettingKey.AccordionDesignAlignmentPanelHeader,
              //   type: BuilderComponentSettingType.HorizontalAlignment,
              //   label: 'Panel Header',
              // },
              // {
              //   id: ComponentSettingKey.AccordionDesignAlignmentPanelContent,
              //   type: BuilderComponentSettingType.HorizontalAlignment,
              //   label: 'Panel Content',
              // },
              {
                id: ComponentSettingKey.AccordionDesignAlignmentGroupHeader,
                type: BuilderComponentSettingType.HorizontalAlignment,
                label: LABELS.GROUP_HEADER_KEY,
              },
              {
                id: ComponentSettingKey.AccordionDesignAlignmentTitleAccordion,
                type: BuilderComponentSettingType.HorizontalAlignment,
                label: 'Title',
                defaultValue: 'start',
              },
              {
                id: ComponentSettingKey.AccordionDesignAlignmentContentAccordion,
                type: BuilderComponentSettingType.HorizontalAlignment,
                label: 'Content',
                defaultValue: 'start',
              },
              {
                id: ComponentSettingKey.AccordionDesignGroupSearchAlign,
                type: BuilderComponentSettingType.HorizontalAlignment,
                label: LABELS.SEARCH_BOX_KEY,
              },
            ],
          },
          [LABELS.TYPOGRAPHY_KEY]: makeTypographyGroup(ComponentSettingKey.AccordionDesignTypography, [
            {
              id: ComponentSettingKey.AccordionDesignTitleTypography,
              label: LABELS.COMPONENT_TITLE_KEY,
              defaultValue: 'Body Large',
            },
            {
              id: ComponentSettingKey.AccordionDesignHeaderTypography,
              label: LABELS.HEADER_KEY,
              defaultValue: 'Body Large',
            },
            {
              id: ComponentSettingKey.AccordionDesignContentTypography,
              label: 'Content',
              defaultValue: 'Body Large',
            },
            {
              id: ComponentSettingKey.AccordionDesignGroupHeaderTypography,
              label: 'Group Header',
              defaultValue: 'Body Medium',
            },
          ]),
          'Items per Page': {
            items: [
              {
                id: ComponentSettingKey.AccordionDesignItemsPerPage,
                label: 'Items per Page',
                type: BuilderComponentSettingType.Slider,
                defaultValue: [100],
                sliderSettings: {
                  max: 100,
                  min: 1,
                  step: 1,
                },
              },
            ],
          },
          [LABELS.TEXT_COLORS_OPTION_KEY]: {
            items: [
              {
                id: ComponentSettingKey.AccordionDesignGroupTextColor,
                type: BuilderComponentSettingType.Group,
                label: '',
                groupItems: {
                  [LABELS.HEADER_KEY]: [
                    ...makeTextColorSettings(
                      ComponentSettingKey.AccordionDesignTextColor,
                      ComponentSettingKey.AccordionDesignTextColorSolid,
                      ComponentSettingKey.AccordionDesignTextColorStartGradient,
                      ComponentSettingKey.AccordionDesignTextColorEndGradient,
                      ComponentSettingKey.AccordionDesignTextColorGradientDirection,
                      ComponentSettingKey.AccordionDesignScheme,
                      LABELS.HEADER_KEY,
                      'adaptive'
                    ).items,
                  ],
                  [LABELS.CONTENT_KEY]: [
                    ...makeTextColorSettings(
                      ComponentSettingKey.AccordionDesignContentColor,
                      ComponentSettingKey.AccordionDesignContentColorSolid,
                      ComponentSettingKey.AccordionDesignContentColorStartGradient,
                      ComponentSettingKey.AccordionDesignContentColorEndGradient,
                      ComponentSettingKey.AccordionDesignContentColorGradientDirection,
                      ComponentSettingKey.AccordionDesignScheme,
                      LABELS.CONTENT_KEY,
                      'adaptive'
                    ).items,
                  ],
                  [LABELS.GROUP_HEADER_KEY]: [
                    ...makeTextColorSettings(
                      ComponentSettingKey.AccordionDesignGroupHeaderColor,
                      ComponentSettingKey.AccordionDesignGroupHeaderColorSolid,
                      ComponentSettingKey.AccordionDesignGroupHeaderColorStartGradient,
                      ComponentSettingKey.AccordionDesignGroupHeaderColorEndGradient,
                      ComponentSettingKey.AccordionDesignGroupHeaderColorGradientDirection,
                      ComponentSettingKey.AccordionDesignScheme,
                      LABELS.GROUP_HEADER_KEY,
                      'adaptive'
                    ).items,
                  ],
                  [LABELS.COMPONENT_TITLE_KEY]: [
                    ...makeTextColorSettings(
                      ComponentSettingKey.AccordionDesignTitleColor,
                      ComponentSettingKey.AccordionDesignTitleColorSolid,
                      ComponentSettingKey.AccordionDesignTitleColorStartGradient,
                      ComponentSettingKey.AccordionDesignTitleColorEndGradient,
                      ComponentSettingKey.AccordionDesignTitleColorGradientDirection,
                      ComponentSettingKey.AccordionDesignScheme,
                      LABELS.COMPONENT_TITLE_KEY,
                      'adaptive'
                    ).items,
                  ],
                },
              },
            ],
          },
          [LABELS.CORNER_RADIUS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.AccordionDesignSearchCornerRadius,
                type: BuilderComponentSettingType.Select,
                label: '',
                defaultValue: MdShape.Medium,
                selectSettings: {
                  options: Object.entries(MdShape).map(([label, value]) => ({
                    value,
                    label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
                  })),
                },
              },
            ],
          },
          [LABELS.HOVER_EFFECT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.AccordionDesignHoverEffect,
                type: BuilderComponentSettingType.Select,
                label: LABELS.HOVER_EFFECT_KEY,
                selectSettings: {
                  options: hoverEffectOptions,
                },
              },
            ],
          },
        },
        action: [
          {
            id: ComponentSettingKey.AccordionActionAddButtonClick,
            label: 'On Add Button Click',
            isSimple: false,
            showLabel: true,
            defaultValue: [
              {
                type: IActionType.FormAddItemScreen,
                settings: {
                  screenType: 'overlay',
                },
              },
            ],
            dataSource: ComponentSettingKey.AccordionDataSource,
            actionTypes: [IActionType.FormAddItemScreen],
          },
        ],
        behavior: {
          // visibility: {
          //   enabled: true,
          //   defaultValue: {
          //     isAny: false,
          //     conditions: [
          //       {
          //         lhs: {
          //           col: 'PROFILE_ROLE',
          //           flag: 8,
          //         },
          //         operator: 'text-is-equal-to',
          //         rhs: {
          //           flag: 4,
          //           value: 'admin',
          //         },
          //       },
          //     ],
          //   },
          // },
          visibility: true,

          custom: {
            Panel: {
              items: [
                {
                  id: ComponentSettingKey.AccordionBehaviorMultiExpand,
                  label: 'Multiple Expansion',
                  type: BuilderComponentSettingType.Switch,
                },
              ],
            },
            [LABELS.USER_SEARCH_KEY]: {
              items: [
                {
                  id: ComponentSettingKey.AccordionBehaviorUserSearch,
                  type: BuilderComponentSettingType.Switch,
                  defaultValue: true,
                  label: LABELS.ENABLE_USER_SEARCH,
                },
                {
                  id: ComponentSettingKey.AccordionBehaviorSearchResultsInfo,
                  type: BuilderComponentSettingType.Switch,
                  defaultValue: true,
                  label: LABELS.SHOW_SEARCH_RESULTS_INFO_KEY,
                },
              ],
            },
            // TODO: Temporary hiding this feature, re-enable this later
            // See: https://trello.com/c/uFU3tBp4/1304-table-component
            // [LABELS.APP_FILTERS_KEY]: makeUserControlledFilter(
            //   ComponentSettingKey.AccordionBehaviorUserFilter,
            //   ComponentSettingKey.AccordionDataSource
            // ),
            [LABELS.ADD_BUTTON_KEY]: {
              items: [
                {
                  id: ComponentSettingKey.AccordionBehaviorAddButtonShow,
                  type: BuilderComponentSettingType.Switch,
                  label: 'Show',
                  defaultValue: true,
                },
                {
                  id: ComponentSettingKey.AccordionBehaviorAddButtonPosition,
                  type: BuilderComponentSettingType.Select,
                  label: 'Button Position',
                  defaultValue: 'inline',
                  forceHorizontal: true,
                  selectSettings: {
                    options: [
                      {
                        value: 'inline',
                        label: 'Inline',
                      },
                      {
                        value: 'bottom',
                        label: 'Bottom',
                      },
                    ],
                  },
                },
                {
                  id: ComponentSettingKey.AccordionBehaviorAddButtonVisibility,
                  type: BuilderComponentSettingType.Visibility,
                  label: 'Show Add Button',
                  defaultValue: {
                    enabled: true,
                    conditions: [
                      {
                        lhs: {
                          col: 'PROFILE_ROLE',
                          flag: ConditionFeatureFlag.ProfileData,
                        },
                        operator: 'text-is-equal-to',
                        rhs: {
                          flag: 4,
                          value: 'admin',
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        },
      },
    },

    // multi-location
    {
      compId: 'multi-location',
      title: LABELS.LOCATIONS_KEY,
      searchKeywords: ['locations', 'address', 'map list', 'store locator', 'nearby places', 'branches', 'shops'],
      icon: 'Routing24',
      nested: false,
      //detailDataSource: undefined,
      settings: {
        dataGroup: {
          [LABELS.DATA_SOURCE_KEY]: {
            items: [
              makeDataSource(ComponentSettingKey.MultiLocationDataSource),
              {
                id: ComponentSettingKey.MultiLocationDataAddress,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.COORDINATES_KEY,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.MultiLocationDataSource,
                  columnTypes: [ColumnTypes.Coordinate],
                },
                help: 'Accept column type Coordinates',
              },
              {
                id: ComponentSettingKey.MultiLocationDataMarker,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.MARKER_ICON_KEY,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.MultiLocationDataSource,
                  columnTypes: [ColumnTypes.Icon, ColumnTypes.Lookup],
                  includeLookupType: true,
                },
                help: 'Accept column type Image / Icon',
              },
              {
                id: ComponentSettingKey.MultiLocationDataHeader,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.HEADER_KEY,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.MultiLocationDataSource,
                  columnTypes: [ColumnTypes.Text],
                },
                help: 'Accept column type Text',
              },
              {
                id: ComponentSettingKey.MultiLocationDataSubHeader,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.SUB_HEADER_KEY,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.MultiLocationDataSource,
                  columnTypes: withoutSlugColumnTypes,
                },
                help: 'Accept column type Text',
              },
              {
                id: ComponentSettingKey.MultiLocationDataDescription,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.DESCRIPTION_KEY,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.MultiLocationDataSource,
                  columnTypes: [ColumnTypes.Text, ColumnTypes.RichText],
                },
                help: 'Accept column type Text / Rich Text',
              },
              {
                id: ComponentSettingKey.MultiLocationDataAvatar,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.AVATAR_KEY,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.MultiLocationDataSource,
                  columnTypes: [ColumnTypes.Image, ColumnTypes.Lookup],
                  includeLookupType: true,
                },
                help: 'Accept column type Image',
              },
              {
                id: ComponentSettingKey.MultiLocationDataImage,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.IMAGE_KEY,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.MultiLocationDataSource,
                  columnTypes: [ColumnTypes.Image, ColumnTypes.Lookup],
                  includeLookupType: true,
                },
                help: 'Accept column type Image',
              },
            ],
          },
          [LABELS.DATA_FILTER_KEY]: {
            items: [
              {
                id: ComponentSettingKey.MultiLocationDataFilter,
                label: LABELS.FILTER_KEY,
                type: BuilderComponentSettingType.ColumnFilter,
                columnFilterSettings: {
                  tableSourceId: ComponentSettingKey.MultiLocationDataSource,
                },
              },
            ],
          },
        },
        designGroup: {
          [LABELS.COLORS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.MultiLocationDesignScheme,
                type: BuilderComponentSettingType.ColorVariant,
                label: LABELS.INFO_BUBBLE_COLOR_VARIANT_KEY,
                defaultValue: 'primary-90',
                colorVariantSettings: {
                  fullColor: 'bg',
                  schemeId: ComponentSettingKey.LocationDesignScheme,
                },
              },
              {
                id: ComponentSettingKey.MultiLocationDesignColorMarker,
                type: BuilderComponentSettingType.ColorVariant,
                label: LABELS.COLOR_MARKER_KEY,
                defaultValue: 'primary-90',
                colorVariantSettings: {
                  fullColor: 'bg',
                  schemeId: ComponentSettingKey.LocationDesignScheme,
                },
              },
            ],
          },
          [LABELS.CLUSTER_KEY]: {
            items: [
              {
                id: ComponentSettingKey.MultiLocationDesignCluster,
                type: BuilderComponentSettingType.Switch,
                label: LABELS.MARKER_CLUSTERING_KEY,
                defaultValue: true,
              },
            ],
          },
          [LABELS.CORNER_RADIUS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.MultiLocationDesignShape,
                type: BuilderComponentSettingType.Select,
                label: '',
                defaultValue: MdShape.None,
                selectSettings: {
                  options: Object.entries(MdShape).map(([label, value]) => ({
                    value,
                    label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
                  })),
                },
              },
            ],
          },
          [LABELS.DIMENSION_KEY]: {
            items: [
              ...makeClampWidthItems(ComponentSettingKey.MultiLocationDesignWidth).items,
              {
                id: ComponentSettingKey.MultiLocationDesignHeight,
                type: BuilderComponentSettingType.Slider,
                label: LABELS.HEIGHT_KEY,
                defaultValue: [400],
                sliderSettings: {
                  max: 980,
                  min: 100,
                  step: 1,
                  unit: 'px',
                },
              },
            ],
          },
          [LABELS.ALIGNMENT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.MultiLocationDesignAlignment,
                type: BuilderComponentSettingType.ComponentAlignment,
                label: LABELS.COMPONENT_ALIGNMENT_KEY,
              },
            ],
          },
          [LABELS.SPACING_KEY]: {
            items: [
              {
                id: ComponentSettingKey.MultiLocationDesignMargin,
                type: BuilderComponentSettingType.Spacing,
                label: LABELS.MARGIN_KEY,
                defaultValue: {
                  top: {
                    value: 16,
                    unit: SpacingInputUnit.Pixel,
                  },
                  bottom: {
                    value: 16,
                    unit: SpacingInputUnit.Pixel,
                  },
                  left: {
                    value: 16,
                    unit: SpacingInputUnit.Pixel,
                  },
                  right: {
                    value: 16,
                    unit: SpacingInputUnit.Pixel,
                  },
                },
                // spacingSettings: {
                //   max: {
                //     [SpacingInputUnit.Em]: 4,
                //     [SpacingInputUnit.Rem]: 4,
                //     [SpacingInputUnit.Pixel]: Infinity,
                //     [SpacingInputUnit.Percent]: 10,
                //   },
                // },
              },
            ],
          },
          [LABELS.ITEMS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.MultiLocationDataNumItems,
                label: 'Dataset Limit',
                type: BuilderComponentSettingType.DataSetLimit,
                defaultValue: {
                  enabled: false,
                  limit: 100,
                  value: 10,
                },
              },
            ],
          },
          // [LABELS.HOVER_EFFECT_KEY]: {
          //   items: [
          //     {
          //       id: ComponentSettingKey.MultiLocationDesignHoverEffect,
          //       type: BuilderComponentSettingType.Select,
          //       label: LABELS.HOVER_EFFECT_KEY,
          //       selectSettings: {
          //         options: hoverEffectOptions,
          //       },
          //     },
          //   ],
          // },
        },
        action: [
          {
            id: ComponentSettingKey.MultiLocationActionOnClick,
            label: LABELS.ON_BUBBLE_CLICK_KEY,
            isSimple: false,
          },
        ],
        behavior: {
          custom: {
            [LABELS.MAP_OPTIONS_KEY]: {
              items: [
                {
                  id: ComponentSettingKey.MultiLocationBehaviorMapTypeControl,
                  type: BuilderComponentSettingType.Switch,
                  label: LABELS.MAP_TYPE_CONTROL_KEY,
                  defaultValue: true,
                },
                {
                  id: ComponentSettingKey.MultiLocationBehaviorFullscreenControl,
                  type: BuilderComponentSettingType.Switch,
                  label: LABELS.FULLSCREEN_CONTROL_KEY,
                  defaultValue: true,
                },
                {
                  id: ComponentSettingKey.MultiLocationBehaviorStreetViewControl,
                  type: BuilderComponentSettingType.Switch,
                  label: LABELS.STREET_VIEW_CONTROL_KEY,
                  defaultValue: true,
                },
                {
                  id: ComponentSettingKey.MultiLocationBehaviorZoomControl,
                  type: BuilderComponentSettingType.Switch,
                  label: LABELS.ZOOM_CONTROL_KEY,
                  defaultValue: true,
                },
                {
                  id: ComponentSettingKey.MultiLocationBehaviorZoomLevel,
                  type: BuilderComponentSettingType.Select,
                  label: LABELS.DEFAULT_ZOOM_KEY,
                  forceHorizontal: true,
                  defaultValue: 'near',
                  selectSettings: {
                    options: [
                      { value: 'near', label: 'Near' },
                      { value: 'medium', label: 'Medium' },
                      { value: 'far', label: 'Far' },
                    ],
                  },
                },
                {
                  id: ComponentSettingKey.MultiLocationBehaviorInfoBubble,
                  type: BuilderComponentSettingType.Select,
                  label: LABELS.INFO_BUBBLE_KEY,
                  forceHorizontal: true,
                  defaultValue: 'default',
                  selectSettings: {
                    options: [
                      { value: 'default', label: 'Default' },
                      { value: 'style-1', label: 'Style 1' },
                      { value: 'style-2', label: 'Style 2' },
                      { value: 'style-3', label: 'Style 3' },
                      { value: 'style-4', label: 'Style 4' },
                    ],
                  },
                },
              ],
            },
            // TODO: Temporary hiding this feature, re-enable this later
            // See: https://trello.com/c/uFU3tBp4/1304-table-component
            // [LABELS.APP_FILTERS_KEY]: makeUserControlledFilter(
            //   ComponentSettingKey.MultiLocationBehaviorUserFilter,
            //   ComponentSettingKey.MultiLocationDataSource
            // ),
          },
          visibility: true,
        },
      },
    },

    // shopping cart
    {
      compId: 'shopping-cart',
      title: 'Shopping Cart',
      searchKeywords: [
        'cart',
        'basket',
        'checkout',
        'order',
        'e-commerce',
        'shop',
        'buy',
        'add to cart',
        'shopping bag',
      ],
      icon: 'shopping_cart',
      nested: false,
      settings: {
        dataGroup: {
          [LABELS.DATA_SOURCE_KEY]: {
            items: [
              makeDataSource(
                ComponentSettingKey.ShoppingCartDataSource,
                'Select a table from your database to serve as the data source for this shopping cart. This will determine the available data for other selections, such as product name, image, price, etc.'
              ),
              {
                id: ComponentSettingKey.ShoppingCartDataProductName,
                type: BuilderComponentSettingType.ColumnSelect,
                label: 'Product Name',
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.ShoppingCartDataSource,
                  columnTypes: [ColumnTypes.Text, ColumnTypes.Template, ColumnTypes.Lookup],
                  includeLookupType: true,
                },
              },
              {
                id: ComponentSettingKey.ShoppingCartDataProductImage,
                type: BuilderComponentSettingType.ColumnSelect,
                label: 'Product Image',
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.ShoppingCartDataSource,
                  columnTypes: [ColumnTypes.Image, ColumnTypes.Lookup],
                  includeLookupType: true,
                },
              },
              {
                id: ComponentSettingKey.ShoppingCartDataProductInfo,
                type: BuilderComponentSettingType.ColumnSelect,
                label: 'More Info',
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.ShoppingCartDataSource,
                  columnTypes: [ColumnTypes.Text, ColumnTypes.DateTime, ColumnTypes.Template, ColumnTypes.Lookup],
                  includeLookupType: true,
                },
              },
              {
                id: ComponentSettingKey.ShoppingCartDataProductStockStatus,
                type: BuilderComponentSettingType.ColumnSelect,
                label: 'Stock Status',
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.ShoppingCartDataSource,
                  columnTypes: [ColumnTypes.Text, ColumnTypes.Template, ColumnTypes.Lookup, ColumnTypes.Function],
                  includeLookupType: true,
                },
              },
              {
                id: ComponentSettingKey.ShoppingCartDataProductPrice,
                type: BuilderComponentSettingType.ColumnSelect,
                label: 'Price',
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.ShoppingCartDataSource,
                  columnTypes: [ColumnTypes.Number, ColumnTypes.Lookup],
                  includeLookupType: true,
                },
              },
              {
                id: ComponentSettingKey.ShoppingCartDataProductQuantity,
                type: BuilderComponentSettingType.ColumnSelect,
                label: 'Quantity',
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.ShoppingCartDataSource,
                  columnTypes: [ColumnTypes.Number, ColumnTypes.Lookup],
                  includeLookupType: true,
                },
              },
            ],
          },
          [LABELS.COMPONENT_TITLE_KEY]: {
            items: [
              {
                id: ComponentSettingKey.ShoppingCartDataCompTitle,
                type: BuilderComponentSettingType.VariableText,
                label: '',
                defaultValue: [
                  {
                    type: 'paragraph',
                    children: [{ text: '' }],
                  },
                ],
                variableTextSettings: {
                  flags:
                    VARIABLE_TEXT_FEATURE_FLAG_ALL &
                    ~VariableTextFeatureFlag.ColumnData &
                    ~VariableTextFeatureFlag.Image,
                },
              },
            ],
          },
          [LABELS.DATA_FILTER_KEY]: makeFilter(
            ComponentSettingKey.ShoppingCartDataFilter,
            ComponentSettingKey.ShoppingCartDataSource
          ),
          [LABELS.SORTING_KEY]: makeSorting(
            ComponentSettingKey.ShoppingCartDataSortBy,
            ComponentSettingKey.ShoppingCartDataSortType,
            ComponentSettingKey.ShoppingCartDataSource
          ),
        },
        designGroup: {
          [LABELS.ALIGNMENT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.ShoppingCartDesignCompHAlign,
                type: BuilderComponentSettingType.ComponentAlignment,
                defaultValue: 'parent',
                label: LABELS.COMPONENT_ALIGNMENT_KEY,
              },
              {
                id: ComponentSettingKey.ShoppingCartDesignCompTitleAlign,
                type: BuilderComponentSettingType.HorizontalAlignment,
                defaultValue: 'start',
                label: LABELS.COMPONENT_TITLE_KEY,
              },
            ],
          },
          [LABELS.COLORS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.ShoppingCartDesignBgColor,
                type: BuilderComponentSettingType.ColorVariant,
                label: LABELS.BACKGROUND_KEY,
                defaultValue: 'primary-80',
              },
              {
                id: ComponentSettingKey.ShoppingCartDesignPrimaryButtonColor,
                type: BuilderComponentSettingType.ColorVariant,
                label: 'Checkout Button',
                defaultValue: 'primary-20',
              },
              {
                id: ComponentSettingKey.ShoppingCartDesignSecondaryButtonColor,
                type: BuilderComponentSettingType.ColorVariant,
                label: 'Continue Button',
                defaultValue: 'primary-90',
              },
            ],
          },
          [LABELS.DIMENSION_KEY]: {
            items: [
              ...makeClampWidthItems(ComponentSettingKey.ShoppingCartDesignWidth).items,
              {
                type: BuilderComponentSettingType.Slider,
                label: 'Image Width',
                help: '',
                id: ComponentSettingKey.ShoppingCartDesignImageWidth,
                clampInAllDevices: true,
                sliderSettings: {
                  range: false,
                  max: 250,
                  min: 80,
                  step: 1,
                  unit: 'px',
                  showUnit: false,
                },
              },
            ],
          },
          [LABELS.SPACING_KEY]: {
            items: [
              {
                id: ComponentSettingKey.ShoppingCartDesignMargin,
                type: BuilderComponentSettingType.Spacing,
                label: LABELS.COMPONENT_MARGIN_KEY,
              },
              {
                id: ComponentSettingKey.ShoppingCartDesignCompTitleMargin,
                type: BuilderComponentSettingType.Spacing,
                label: LABELS.COMPONENT_TITLE_MARGIN_KEY,
              },
            ],
          },
          [LABELS.CORNER_RADIUS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.ShoppingCartDesignCornerRadius,
                label: 'Cart Container',
                type: BuilderComponentSettingType.Select,
                defaultValue: MdShape.Medium,
                selectSettings: {
                  options: Object.entries(MdShape).map(([label, value]) => ({
                    value,
                    label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
                  })),
                },
              },
              {
                id: ComponentSettingKey.ShoppingCartDesignImageRadius,
                label: 'Image Radius',
                type: BuilderComponentSettingType.Select,
                defaultValue: MdShape.Medium,
                selectSettings: {
                  options: Object.entries(MdShape).map(([label, value]) => ({
                    value,
                    label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
                  })),
                },
              },
              {
                id: ComponentSettingKey.ShoppingCartDesignButtonRadius,
                label: 'Button Radius',
                type: BuilderComponentSettingType.Select,
                defaultValue: MdShape.Medium,
                selectSettings: {
                  options: Object.entries(MdShape).map(([label, value]) => ({
                    value,
                    label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
                  })),
                },
              },
            ],
          },
          [LABELS.TYPOGRAPHY_KEY]: makeTypographyGroup(ComponentSettingKey.ShoppingCartDesignTypography, [
            {
              id: ComponentSettingKey.ShoppingCartDesignCompTitleTypography,
              label: LABELS.COMPONENT_TITLE_KEY,
              defaultValue: 'headline-extra-small',
            },
            {
              id: ComponentSettingKey.ShoppingCartDesignTitleTypography,
              label: 'Product Name',
              defaultValue: 'body-medium',
            },
            {
              id: ComponentSettingKey.ShoppingCartDesignInfoTypography,
              label: 'More Info',
              defaultValue: 'body-extra-small',
            },
          ]),
          [LABELS.HOVER_EFFECT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.ShoppingCartDesignHoverEffect,
                type: BuilderComponentSettingType.Select,
                label: LABELS.HOVER_EFFECT_KEY,
                selectSettings: {
                  options: hoverEffectOptions,
                },
              },
            ],
          },
        },
        action: [
          {
            id: ComponentSettingKey.ShoppingCartActionItemClick,
            label: LABELS.ON_ITEM_CLICK_KEY,
            isSimple: false,
            defaultValue: [
              {
                type: IActionType.ShowDetailsScreen,
                settings: {},
              },
            ],
            actionTypes: [IActionType.ShowDetailsScreen],
          },
          {
            id: ComponentSettingKey.ShoppingCartActionQtyIncrement,
            label: 'On Increase Quantity',
            isSimple: false,
            defaultValue: [
              {
                type: IActionType.IncrementValue,
                settings: {},
              },
            ],
          },
          {
            id: ComponentSettingKey.ShoppingCartActionQtyDecrement,
            label: 'On Decrease Quantity',
            isSimple: false,
            defaultValue: [
              {
                type: IActionType.IncrementValue,
                settings: {},
              },
            ],
          },
          {
            id: ComponentSettingKey.ShoppingCartActionRemoveItem,
            label: 'On Remove Item',
            isSimple: false,
            defaultValue: [
              {
                type: IActionType.DeleteRow,
                settings: {},
              },
            ],
          },
          {
            id: ComponentSettingKey.ShoppingCartActionOnProceed,
            label: 'On Proceed to Checkout',
            isSimple: false,
            defaultValue: [
              {
                type: IActionType.GoToScreen,
                settings: {},
              },
            ],
          },
          {
            id: ComponentSettingKey.ShoppingCartActionOnBack,
            label: 'On Continue Shopping',
            isSimple: false,
            defaultValue: [
              {
                type: IActionType.GoToScreen,
                settings: {},
              },
            ],
          },
        ],
        behavior: {
          custom: {
            [LABELS.CART_OPTIONS_KEY]: {
              items: [
                {
                  id: ComponentSettingKey.ShoppingCartBehaviorAllowQtyChange,
                  type: BuilderComponentSettingType.Switch,
                  label: 'Allow Quantity Change',
                  defaultValue: true,
                },
                {
                  id: ComponentSettingKey.ShoppingCartBehaviorAllowRemoveItem,
                  type: BuilderComponentSettingType.Switch,
                  label: 'Allow Remove Item',
                  defaultValue: true,
                },
              ],
            },
          },
          visibility: true,
        },
      },
    },

    // timeline
    {
      compId: 'timeline',
      title: 'Timeline',
      searchKeywords: ['timeline', 'steps', 'progress steps', 'journey', 'history', 'sequence', 'schedule', 'process'],
      icon: 'view_timeline',
      nested: false,
      settings: {
        dataGroup: {
          [LABELS.DATA_SOURCE_KEY]: {
            items: [
              makeDataSource(ComponentSettingKey.TimelineDataSource),
              {
                id: ComponentSettingKey.TimelineDataTitle,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.TITLE_KEY,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.TimelineDataSource,
                  columnTypes: [
                    ColumnTypes.Text,
                    ColumnTypes.RichText,
                    ColumnTypes.Function,
                    ColumnTypes.URL,
                    ColumnTypes.Email,
                    ColumnTypes.Address,
                    ColumnTypes.Boolean,
                    ColumnTypes.Coordinate,
                    ColumnTypes.DateTime,
                    ColumnTypes.Join,
                    ColumnTypes.Color,
                    ColumnTypes.Number,
                    ColumnTypes.Phone,
                    ColumnTypes.RowID,
                    ColumnTypes.AISEO,
                    ColumnTypes.AITextProcessing,
                  ],
                },
              },
              {
                id: ComponentSettingKey.TimelineDataContent,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.CONTENT_KEY,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.TimelineDataSource,
                  columnTypes: [
                    ColumnTypes.Text,
                    ColumnTypes.RichText,
                    ColumnTypes.Function,
                    ColumnTypes.URL,
                    ColumnTypes.Email,
                    ColumnTypes.Address,
                    ColumnTypes.Boolean,
                    ColumnTypes.Coordinate,
                    ColumnTypes.DateTime,
                    ColumnTypes.Join,
                    ColumnTypes.Color,
                    ColumnTypes.Number,
                    ColumnTypes.Phone,
                    ColumnTypes.RowID,
                    ColumnTypes.AISEO,
                    ColumnTypes.AITextProcessing,
                  ],
                },
              },
              {
                id: ComponentSettingKey.TimelineDataDate,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.DATE_KEY,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.TimelineDataSource,
                  columnTypes: [
                    ColumnTypes.Text,
                    ColumnTypes.RichText,
                    ColumnTypes.Function,
                    ColumnTypes.URL,
                    ColumnTypes.Email,
                    ColumnTypes.Address,
                    ColumnTypes.Boolean,
                    ColumnTypes.Coordinate,
                    ColumnTypes.DateTime,
                    ColumnTypes.Join,
                    ColumnTypes.Color,
                    ColumnTypes.Number,
                    ColumnTypes.Phone,
                    ColumnTypes.RowID,
                    ColumnTypes.AISEO,
                    ColumnTypes.AITextProcessing,
                  ],
                },
              },
              {
                id: ComponentSettingKey.TimelineDataIcon,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.ICON_KEY,
                defaultValue: {
                  flag: VariableSelectFeatureFlag.Icon,
                  value: 'chevron_right',
                },
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.TimelineDataSource,
                  columnTypes: [ColumnTypes.Icon, ColumnTypes.Lookup],
                },
              },
            ],
          },
          [LABELS.GROUPING_KEY]: {
            items: [
              {
                id: ComponentSettingKey.TimelineDataGroupBy,
                label: LABELS.GROUP_BY_KEY,
                help: 'Only works when in vertical layout mode',
                type: BuilderComponentSettingType.ColumnSelect,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.TimelineDataSource,
                  noAutoSelect: true,
                  includeLookupType: true,
                  columnTypes: [
                    ColumnTypes.Text,
                    ColumnTypes.RichText,
                    ColumnTypes.Function,
                    ColumnTypes.URL,
                    ColumnTypes.Email,
                    ColumnTypes.Address,
                    ColumnTypes.Boolean,
                    ColumnTypes.Coordinate,
                    ColumnTypes.DateTime,
                    ColumnTypes.Join,
                    ColumnTypes.Color,
                    ColumnTypes.Number,
                    ColumnTypes.Phone,
                    ColumnTypes.RowID,
                    ColumnTypes.AISEO,
                    ColumnTypes.AITextProcessing,
                  ],
                },
              },
            ],
          },
          [LABELS.SORTING_KEY]: makeSorting(
            ComponentSettingKey.TimelineDataSortBy,
            ComponentSettingKey.TimelineDataSortType,
            ComponentSettingKey.TimelineDataSource
          ),
          [LABELS.DATA_FILTER_KEY]: makeFilter(
            ComponentSettingKey.TimelineDataFilter,
            ComponentSettingKey.TimelineDataSource
          ),
        },
        designGroup: {
          [LABELS.TYPOGRAPHY_KEY]: makeTypographyGroup(ComponentSettingKey.TimelineDesignTypography, [
            {
              id: ComponentSettingKey.TimelineDesignGroupTitleTypography,
              label: LABELS.GROUP_TITLE_KEY,
              defaultValue: 'Headline Extra Small',
            },
            {
              id: ComponentSettingKey.TimelineDesignTitleTypography,
              label: LABELS.TITLE_KEY,
              defaultValue: 'Title Small',
            },
            {
              id: ComponentSettingKey.TimelineDesignContentTypography,
              label: LABELS.CONTENT_KEY,
              defaultValue: 'Body Small',
            },
            {
              id: ComponentSettingKey.TimelineDesignDateTypography,
              label: LABELS.DATE_KEY,
              defaultValue: 'Body Small',
            },
          ]),
          [LABELS.ORIENTATION_KEY]: {
            items: [
              {
                id: ComponentSettingKey.TimelineDesignOrientation,
                type: BuilderComponentSettingType.IconSelect,
                label: 'Orientation',
                defaultValue: 'vertical',
                help: 'Horizontal orientation will make the list scrollable horizontally.',
                iconSelectSettings: {
                  options: [
                    {
                      id: 'horizontal',
                      icon: 'Horizontal24',
                      title: 'Horizontal',
                    },
                    {
                      id: 'vertical',
                      icon: 'Vertical24',
                      title: 'Vertical',
                    },
                  ],
                },
              },
            ],
          },
          [LABELS.COLORS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.TimelineDesignIconColor,
                type: BuilderComponentSettingType.ColorVariant,
                label: LABELS.ICON_COLOR_KEY,
                defaultValue: 'primary-30',
                colorVariantSettings: {
                  fullColor: 'bg',
                  schemeId: ComponentSettingKey.TimelineDesignScheme,
                },
              },
              {
                id: ComponentSettingKey.TimelineDesignIconBackgroundColorVariant,
                type: BuilderComponentSettingType.ColorVariant,
                label: LABELS.ICON_BACKGROUND_COLOR_KEY,
                defaultValue: 'primary-80',
                colorVariantSettings: {
                  fullColor: 'bg',
                  schemeId: ComponentSettingKey.TimelineDesignScheme,
                },
              },
              {
                id: ComponentSettingKey.TimelineDesignIconColorCustom,
                type: BuilderComponentSettingType.Switch,
                label: 'Dynamic Color',
                defaultValue: false,
              },
              {
                id: ComponentSettingKey.TimelineDesignIconColorColumn,
                type: BuilderComponentSettingType.ColumnSelect,
                label: 'Select Color Column',
                help: 'If no color found will fallback to the color variant settings.',
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.TimelineDataSource,
                  columnTypes: [ColumnTypes.Color, ColumnTypes.Lookup],
                },
                dependOn: {
                  id: ComponentSettingKey.TimelineDesignIconColorCustom,
                  showWhen: (v) => v === true,
                },
              },
              {
                id: ComponentSettingKey.TimelineDesignTimelineLineColor,
                type: BuilderComponentSettingType.ColorVariant,
                label: LABELS.TIMELINE_LINE_COLOR_KEY,
                defaultValue: 'primary-70',
                colorVariantSettings: {
                  fullColor: 'bg',
                  schemeId: ComponentSettingKey.TimelineDesignScheme,
                },
              },
            ],
          },
          [LABELS.SPACING_KEY]: {
            items: [
              {
                id: ComponentSettingKey.TimelineDesignMargin,
                type: BuilderComponentSettingType.Spacing,
                label: 'Margin',
                defaultValue: {
                  top: {
                    value: 8,
                    unit: SpacingInputUnit.Pixel,
                  },
                  bottom: {
                    value: 8,
                    unit: SpacingInputUnit.Pixel,
                  },
                  left: {
                    value: 10,
                    unit: SpacingInputUnit.Pixel,
                  },
                  right: {
                    value: 10,
                    unit: SpacingInputUnit.Pixel,
                  },
                },
              },
              {
                type: BuilderComponentSettingType.SpacingSingle,
                label: 'Item Gap',
                id: ComponentSettingKey.TimelineDesignItemGap,
                defaultValue: {
                  value: 24,
                  unit: SpacingInputUnit.Pixel,
                },
              },
            ],
          },
          [LABELS.TEXT_LINES_KEY]: {
            items: [
              {
                type: BuilderComponentSettingType.Slider,
                label: 'Title',
                id: ComponentSettingKey.TimelineDesignTitleLines,
                defaultValue: [1],
                sliderSettings: {
                  max: 4,
                  min: 1,
                  step: 1,
                  range: false,
                  unit: '',
                  showUnit: false,
                },
              },
              {
                type: BuilderComponentSettingType.Slider,
                label: 'Content',
                id: ComponentSettingKey.TimelineDesignContentLines,
                defaultValue: [2],
                sliderSettings: {
                  max: 4,
                  min: 1,
                  step: 1,
                  range: false,
                  unit: '',
                  showUnit: false,
                },
              },
            ],
          },
          [LABELS.ALIGNMENT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.TimelineDesignCompHAlign,
                type: BuilderComponentSettingType.ComponentAlignment,
                defaultValue: 'center',
                label: LABELS.COMPONENT_ALIGNMENT_KEY,
              },
              {
                id: ComponentSettingKey.TimelineDesignPaginationAlignment,
                type: BuilderComponentSettingType.ComponentAlignment,
                defaultValue: 'center',
                label: LABELS.PAGINATION_ALIGNMENT_KEY,
              },
            ],
          },
          [LABELS.DIMENSION_KEY]: {
            items: [...makeClampWidthItems(ComponentSettingKey.TimelineDesignWidth).items],
          },
          [LABELS.ITEMS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.TimelineDataSetLimit,
                label: 'Dataset Limit',
                type: BuilderComponentSettingType.DataSetLimit,
                defaultValue: {
                  enabled: false,
                  limit: 100,
                  value: 10,
                },
              },
              {
                id: ComponentSettingKey.TimelineDesignNumItems,
                label: 'Number of Items',
                type: BuilderComponentSettingType.Number,
                defaultValue: 10,
                numberSettings: {
                  max: 100,
                  min: 1,
                  step: 1,
                },
              },
              {
                id: ComponentSettingKey.TimelineDesignPagination,
                label: LABELS.SHOW_PAGINATION_KEY,
                type: BuilderComponentSettingType.Switch,
                defaultValue: true,
              },
            ],
          },
          [LABELS.TEXT_COLORS_OPTION_KEY]: {
            items: [
              ...makeTextColorSettings(
                ComponentSettingKey.TimelineDesignTitleColor,
                ComponentSettingKey.TimelineDesignTitleColorSolid,
                ComponentSettingKey.TimelineDesignTitleColorStartGradient,
                ComponentSettingKey.TimelineDesignTitleColorEndGradient,
                ComponentSettingKey.TimelineDesignTitleColorGradientDirection,
                ComponentSettingKey.TimelineDesignScheme,
                'Title',
                'adaptive'
              ).items,
              ...makeTextColorSettings(
                ComponentSettingKey.TimelineDesignContentColor,
                ComponentSettingKey.TimelineDesignContentColorSolid,
                ComponentSettingKey.TimelineDesignContentColorStartGradient,
                ComponentSettingKey.TimelineDesignContentColorEndGradient,
                ComponentSettingKey.TimelineDesignContentColorGradientDirection,
                ComponentSettingKey.TimelineDesignScheme,
                'Content',
                'adaptive'
              ).items,
              ...makeTextColorSettings(
                ComponentSettingKey.TimelineDesignDateColor,
                ComponentSettingKey.TimelineDesignDateColorSolid,
                ComponentSettingKey.TimelineDesignDateColorStartGradient,
                ComponentSettingKey.TimelineDesignDateColorEndGradient,
                ComponentSettingKey.TimelineDesignDateColorGradientDirection,
                ComponentSettingKey.TimelineDesignScheme,
                'Date',
                'adaptive'
              ).items,
              ...makeTextColorSettings(
                ComponentSettingKey.TimelineDesignGroupTitleColor,
                ComponentSettingKey.TimelineDesignGroupTitleColorSolid,
                ComponentSettingKey.TimelineDesignGroupTitleColorStartGradient,
                ComponentSettingKey.TimelineDesignGroupTitleColorEndGradient,
                ComponentSettingKey.TimelineDesignGroupTitleColorGradientDirection,
                ComponentSettingKey.TimelineDesignScheme,
                'Group Title',
                'adaptive'
              ).items,
            ],
          },
          [LABELS.HOVER_EFFECT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.TimelineDesignHoverEffect,
                type: BuilderComponentSettingType.Select,
                label: LABELS.HOVER_EFFECT_KEY,
                selectSettings: {
                  options: hoverEffectOptions,
                },
              },
            ],
          },
        },
        action: [
          {
            id: ComponentSettingKey.TimelineActionItemClick,
            label: LABELS.ON_ITEM_CLICK_KEY,
            isSimple: false,
            defaultValue: [
              {
                type: IActionType.ShowDetailsScreen,
                settings: {},
              },
            ],
            actionTypes: [IActionType.ShowDetailsScreen],
          },
        ],
        behavior: {
          custom: {},
          visibility: true,
        },
      },
    },
  ],
}
